import { isRampFoot, isRampHead, LevelSource, rampDirOn } from "./ramps";
import { walkable } from "./pathfind";
import { TILE } from "./sprites";
import { Tile } from "./types";
import { keptContext } from "./paper";

/**
 * The oblique view: the world seen from the south-east and a little above,
 * with every storey of height drawn half a tile up and half a tile to the
 * left. Asked for on 2026-09-27: "implement the oblique view with south and
 * east faces visible".
 *
 * WHAT IT IS. The grid stays square and on the screen's own axes, and every
 * tile sprite is the one it always was, drawn at the size it always was.
 * What changes is that height is drawn: a thing a storey up is drawn
 * STOREY pixels up and STOREY pixels left of where it would lie on the
 * ground, so a block of rock a storey tall shows its top up there and, in
 * the gap it leaves behind, its south face and its east face. The east face
 * is shaded darker than the south one, which is what says the light comes
 * from the south-west and the camera stands to the south-east. It is the
 * view Tibia itself uses, and the one the mockups in the proposal settled
 * on over true isometric: it gets most of what "you can see the height"
 * is worth without turning a straight step into a screen diagonal.
 *
 * WHAT IT IS NOT. It is drawing and nothing else. Every rule of the world
 * is the server's and none of them changed: the keys still step the way
 * they stepped, a diagonal still costs what it cost, a dragon's fire still
 * runs down a row of the grid and the view ring and a lantern's light are
 * still squares — squares that sit up and to the left by however many
 * storeys up they are. Only where things are painted, what order they are
 * painted in, and which tile a click lands on had to learn about height.
 *
 * Everything here is a pure answer about that geometry — which tiles stand
 * a storey tall, where a face goes, which tile a point on the glass is, what
 * order things are painted in — plus the handful of pictures made once per
 * sprite that the faces, the dark and the see-through outlines are drawn
 * with. What is drawn, and when, is still render.ts's to say.
 */

/**
 * How far one storey of height is drawn, in world pixels: this many up and
 * this many to the left. Half a tile, which is what the mockups were shown
 * at and chosen from: at a third of a tile the faces were slivers and the
 * dragons' peaks read nearly flat, and at a whole tile a castle wall hides
 * the whole of the yard behind it.
 */
export const STOREY = TILE / 2;

/**
 * The tiles that stand a storey tall: a block of whatever the tile is, from
 * the floor of its level to the floor of the next, with a top to stand on
 * (or not) and faces down its sides.
 *
 * They are exactly the tiles that stop an arrow — Tile.Opaque on the
 * server — and that is not a coincidence to be tidied away. What the eye
 * cannot see past and what a bow cannot shoot past are one question, and a
 * wall that stood up on the screen but let arrows through, or a knee-high
 * wall that stopped them, would be the picture disagreeing with the rules.
 * Trees are not here: an arrow clears the canopy, and a tree is drawn
 * standing up as a thing on its tile, the way a creature is, rather than
 * as a block of the ground.
 *
 * Anything else — hilltop, rampart, roof, the head of a ramp or a stair,
 * and whatever tiles are added after these — is a floor at the height of
 * its level, which is how a new kind of ground gets drawn right without
 * anybody having to come back here.
 */
// The Shire's hillside and the round doors in it stand as a mass the way
// rock does (2026-10-01): The Hill over Bag End is a hill, not a lawn.
// The frozen waste along the north edge stands as a mass the way rock does
// (2026-10-08: "spiky looking ice mountains"), with spires of ice stood up
// off it (see icespire.ts).
const RAISED = new Set<number>([Tile.Wall, Tile.Rock, Tile.CaveWall, Tile.Palisade, Tile.Hillside, Tile.HobbitDoor, Tile.FrozenWaste]);

/** Whether a tile is drawn as a block a storey tall. */
export function isRaised(t: number | undefined): boolean {
  return t !== undefined && RAISED.has(t);
}

/**
 * Which way a door on (x, y) of level z stands (asked for on 2026-09-27:
 * "fix the door so it fits the oblique style. we might need a east/west and
 * north/south variants of doors"). A doorway is a gap in a wall, and the
 * door stands in the wall's own plane: between blocks west and east of it
 * the wall runs east-west and the door is seen on its south face, "s";
 * between blocks north and south of it the wall runs north-south and it is
 * seen on its east face, "e". A doorway at the end of a wall, with a block
 * on one side only — the Doors of Durin, with the mountain to the south and
 * a tree to the north — follows that one block the same way. With nothing
 * beside it, or blocks on two sides at a corner, it is not in a wall at
 * all and lies flat as it always did: null.
 */
export function doorFacing(shown: Shown, z: number, x: number, y: number): "s" | "e" | null {
  const raised = (dx: number, dy: number) => isRaised(shown(z, x + dx, y + dy));
  const w = raised(-1, 0), e = raised(1, 0), n = raised(0, -1), s = raised(0, 1);
  if (w && e) return "s";
  if (n && s) return "e";
  if ((n || s) && !w && !e) return "e";
  if ((w || e) && !n && !s) return "s";
  return null;
}

/**
 * The transform that lays a picture TILE square onto a face a storey tall
 * whose top edge starts at (X, Y) and runs a tile east (a south face) or a
 * tile south (an east face): the picture's top edge along the top of the
 * face and its bottom edge along the ground, its left edge on the west end
 * of a south face or the north end of an east one. As a canvas transform,
 * a to f.
 *
 * Until the thin walls (2026-09-27: "implement the thin castle walls, alter
 * the position of the base of the wall so minimal or no overlap is required
 * most of the time") (X, Y) was the top-left corner of a whole block's
 * lifted top and the face hung off its far edge. A door now stands in a
 * wall a quarter of a tile thick, whose face is not at the edge of any
 * square, so the caller says where the face's top edge is: see doorPlane.
 */
export function faceTransform(side: "s" | "e", X: number, Y: number): [number, number, number, number, number, number] {
  const k = STOREY / TILE;
  return side === "s" ? [1, 0, k, k, X, Y] : [0, 1, k, k, X, Y];
}

/*
 * WALLS AND ROCK THAT STAY IN THEIR TILES. Asked for on 2026-09-27:
 * "implement the thin castle walls, alter the position of the base of the
 * wall so minimal or no overlap is required most of the time, particularly
 * a southeast corner wall. rework mountain/cave walls for a similar effect."
 *
 * A thing a storey tall has its top drawn half a tile up and left of its
 * foot. Standing on the whole of its tile, as every block did at first, its
 * top reached half a tile over the tiles north and west of it, and what it
 * covered there had to be cut back out wherever that was a floor of its own
 * level. Here the foot is moved instead: everything a raised tile paints —
 * its top and the south and east faces under the top — lies inside that
 * tile's own square on the glass, except where it runs on into a raised
 * neighbour it is joined to, which is raised too. The top keeps to the
 * north-west part of the square and the foot is the top moved half a tile
 * south-east, so nothing needs cutting off the floor beside it and the
 * floor beside it covers nothing.
 *
 * A CASTLE WALL (Tile.Wall) is thin: a post WALL_THICK across whose top is
 * centred a quarter of a tile in from the north and west edges — so its foot
 * is centred a quarter in from the south and east ones — with an arm to
 * each side where the wall carries on. The rest of the square is floor,
 * taken from the floors either side of the wall (groundRegions), or the
 * foot of the rock where the wall is built against rock. A wall two thick
 * is filled solid between its two lines (wallFills).
 *
 * ROCK (Tile.Rock, Tile.CaveWall, Tile.Palisade) stays a mass: its top is
 * its square, less the half tile along a south or east side that looks out
 * on to open ground, and its faces fill that half. Put another way, a mass
 * of rock stands half a tile in from its north and west edges, so its
 * lifted top lands exactly on the edge of its tiles. A floor of the level
 * above lying on the rock is drawn over the top where it always was, a
 * tile of it over the quarters of four tiles of rock, which is what keeps
 * a hilltop or a rampart where it was; rock with such floors on all of it
 * and no face to show is not drawn at all (see render.ts).
 *
 * All of it is pure geometry in a tile's own pixels, with the square at
 * (0, 0)–(TILE, TILE) and the top already lifted, so the tests can run it
 * and the click can ask the same shapes the drawing paints (paintedBy).
 */

/** A point in a tile's own world pixels. */
export type Pt = readonly [number, number];

/**
 * One flat piece of what a raised tile paints, as a convex polygon in its
 * tile's own pixels: a piece of its top, or a south or east face hanging
 * from the top's edge.
 */
export interface Facet {
  side: "top" | "s" | "e";
  pts: Pt[];
  /** A face carried on from the same face of the storey above, which has no lighter lip at its top: see skirtFacets. */
  under?: boolean;
}

/** A line along an edge of a thin wall's top: dark on the north and west, light on the south and east. */
export type Edge = readonly [number, number, number, number, boolean];

/**
 * Everything one raised tile paints: its facets, the outline of its top,
 * and a key that says which of the finitely many shapes it is, for the
 * picture of it to be made once and kept (see pieceOf): 0 to 7 for rock,
 * 3000 on for a door's lintel and face, 10000 to 42767 for a thin wall.
 */
export interface Piece {
  key: number;
  facets: Facet[];
  edges: Edge[];
}

/**
 * How thick a castle wall is, in world pixels: a little under a third of a
 * tile. Thin enough that the floor either side shows, thick enough that the
 * top still reads as a wall rather than a line three zooms out, where it is
 * two or three pixels across.
 */
export const WALL_THICK = 10;
/** Where the middle of a wall's top runs, in from its tile's north or west edge: a quarter of a tile. Its foot runs as far in from the south or east edge. */
export const WALL_MID = TILE / 4;
/** A wall standing on its own, joined to nothing: a pillar, fatter than a wall, or it reads as a stick. */
export const PILLAR = 14;
const WA = WALL_MID - WALL_THICK / 2, WB = WALL_MID + WALL_THICK / 2;
/** Every shape made so far, by key: they are pure, and a frame asks for one per wall and rock in the window. */
const pieces = new Map<number, Piece>();

/**
 * What a thin wall joins on one side: 0 nothing, 1 more wall or a door in
 * the wall's line (the arm runs to the tile's edge and on into it), 2 rock
 * (an arm to the north or west runs on half a tile more, to the rock's top,
 * whose edge is that much further in; see the note above), 3 a doorway
 * under a roof in the wall's line (see roofedDoorway): the arm runs to
 * where the doorway's opening begins, half a tile on into it to the north
 * or west and half a tile short of the edge to the south or east.
 */
export type Join = 0 | 1 | 2 | 3;

const rect = (x0: number, y0: number, x1: number, y1: number): Pt[] => [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
/** The south face under the top's edge from (x0, y) to (x1, y): a storey down, and so a storey to the right. */
const southFace = (x0: number, x1: number, y: number): Facet => ({ side: "s", pts: [[x0, y], [x1, y], [x1 + STOREY, y + STOREY], [x0 + STOREY, y + STOREY]] });
/** The east face under the top's edge from (x, y0) to (x, y1). */
const eastFace = (x: number, y0: number, y1: number): Facet => ({ side: "e", pts: [[x, y0], [x + STOREY, y0 + STOREY], [x + STOREY, y1 + STOREY], [x, y1]] });

/**
 * A thin wall's pieces, from what it joins north, east, south and west: a
 * post, an arm to each side it joins, the south and east faces wherever the
 * top's edge is open, and the outline of the top. The post's top is
 * [WA, WB] square and its foot the same a storey south-east, so a wall that
 * turns a corner — the south-east corner of a room, joined north and west —
 * and a wall that ends paint nothing outside their own square, and a run
 * of wall joins its neighbour's arm edge to edge, top and foot. `fills`
 * says which corners are inside a wall two thick (see wallFills): the
 * south-east one is filled here, and the faces and outline along the
 * others are left out, since they are inside the fill.
 */
export function wallFacets(n: Join, e: Join, s: Join, w: Join, fills = 0, skirt = 0): Piece {
  const key = 10000 + n + e * 4 + s * 16 + w * 64 + fills * 256 + skirt * 4096;
  let p = pieces.get(key);
  if (!p) {
    p = makeWall(key, n, e, s, w, fills);
    if (skirt) p = { key, facets: [...p.facets, ...skirtFacets(skirt)], edges: p.edges };
    pieces.set(key, p);
  }
  return p;
}

/**
 * The outside faces of a building's wall under its roof, bit 1 east and 2
 * south (see wallSkirt): the face of a whole block, as a ledge's (see
 * ledgeFacets), from the roof's edge straight down to the foot of the
 * wall's tile. A thin wall's own face stands a quarter of a tile in from
 * its tile's edge, and the roof over it is a whole tile, so the roof's
 * edge ran on past the top of the wall's face and the face came out from
 * under the roof a few pixels further along, with the eaves' shadow
 * between them (2026-11-02, Matt: "fix the edge of buildings like this",
 * of a roof's corner with the wall's face poking out beside it). These go
 * on after the wall's own, over the part of it that showed.
 */
function skirtFacets(skirt: number): Facet[] {
  const H = TILE / 2;
  const out: Facet[] = [];
  // Under more wall (bit 4) the face carries on the one above it, and
  // the lighter lip a face has where it turns into its top would be a
  // white line between the storeys (2026-11-02, Matt: "fix the white line
  // between levels too").
  const under = (skirt & 4) !== 0;
  if (skirt & 2) out.push({ ...southFace(-H, TILE - H, TILE - H), under });
  if (skirt & 1) out.push({ ...eastFace(TILE - H, -H, TILE - H), under });
  return out;
}
function makeWall(key: number, n: Join, e: Join, s: Join, w: Join, fills: number): Piece {
  if (!n && !e && !s && !w) {
    const a = WALL_MID - PILLAR / 2, b = WALL_MID + PILLAR / 2;
    return {
      key,
      facets: [southFace(a, b, b), eastFace(b, a, b), { side: "top", pts: rect(a, a, b, b) }],
      edges: [[a, a, b, a, true], [a, a, a, b, true], [a, b, b, b, false], [b, a, b, b, false]],
    };
  }
  const nw = (fills & 1) !== 0, ne = (fills & 2) !== 0, sw = (fills & 4) !== 0, se = (fills & 8) !== 0;
  // Where the arms end: at the tile's edges, but for an arm into rock or
  // into a roofed doorway to the north or west, which runs on half a tile
  // past it, and one into a roofed doorway to the south or east, which
  // stops half a tile short of it.
  const n0 = n >= 2 ? -STOREY : 0, w0 = w >= 2 ? -STOREY : 0;
  const e1 = e === 3 ? TILE - STOREY : TILE, s1 = s === 3 ? TILE - STOREY : TILE;
  const facets: Facet[] = [];
  const edges: Edge[] = [];
  // The faces first, so the tops go on over their inner corners: along
  // the foot of the top's south edge and its east edge, piece by piece,
  // leaving out what a filled corner covers, and joined where they meet.
  const runs = (segs: [number, number][]) => {
    const out: [number, number][] = [];
    for (const [a, b] of segs) {
      const last = out[out.length - 1];
      if (last && last[1] === a) last[1] = b;
      else out.push([a, b]);
    }
    return out;
  };
  const south: [number, number][] = [];
  if (w && !sw) south.push([w0, WA]);
  if (!s) south.push([WA, WB]);
  if (e && !se) south.push([WB, e1]);
  for (const [a, b] of runs(south)) facets.push(southFace(a, b, WB));
  const east: [number, number][] = [];
  if (n && !ne) east.push([n0, WA]);
  if (!e) east.push([WA, WB]);
  if (s && !se) east.push([WB, s1]);
  for (const [a, b] of runs(east)) facets.push(eastFace(WB, a, b));
  facets.push({ side: "top", pts: rect(WA, WA, WB, WB) });
  if (n) facets.push({ side: "top", pts: rect(WA, n0, WB, WA) });
  if (e) facets.push({ side: "top", pts: rect(WB, WA, e1, WB) });
  if (s) facets.push({ side: "top", pts: rect(WA, WB, WB, s1) });
  if (w) facets.push({ side: "top", pts: rect(w0, WA, WA, WB) });
  // Wall on three sides of the south-east corner, a block of wall two
  // thick: the hole between the arms is filled, to the posts of the
  // neighbours east, south and south-east, so a thick wall is solid rather
  // than a ladder of rungs.
  if (se) facets.push({ side: "top", pts: rect(WB, WB, TILE + WA, TILE + WA) });
  // The top's outline: the long sides of each arm, and the post's sides
  // where no arm leaves it, except where they border a filled corner.
  if (n) {
    if (!nw) edges.push([WA, n0, WA, WA, true]);
    if (!ne) edges.push([WB, n0, WB, WA, false]);
  } else edges.push([WA, WA, WB, WA, true]);
  if (w) {
    if (!nw) edges.push([w0, WA, WA, WA, true]);
    if (!sw) edges.push([w0, WB, WA, WB, false]);
  } else edges.push([WA, WA, WA, WB, true]);
  if (e) {
    if (!ne) edges.push([WB, WA, e1, WA, true]);
    if (!se) edges.push([WB, WB, e1, WB, false]);
  } else edges.push([WB, WA, WB, WB, false]);
  if (s) {
    if (!sw) edges.push([WA, WB, WA, s1, true]);
    if (!se) edges.push([WB, WB, WB, s1, false]);
  } else edges.push([WA, WB, WB, WB, false]);
  return { key, facets, edges };
}

/**
 * A tile of rock's pieces, from whether the mass it belongs to carries on
 * east of it, south of it and south-east of it. Quarter by quarter, the
 * top is what can be lifted half a tile north-west off rock without
 * leaving rock on the way: the north-west quarter always, the north-east
 * one if there is rock to the east, the south-west one if there is rock to
 * the south, and the south-east one only with rock on all three. Each open
 * south or east edge of that has its face, which fills the quarter below
 * or beside it and never leaves the mass.
 *
 * Only those three neighbours count, which is the whole point: whatever
 * lies north or west of a rock, the rock paints nothing over it.
 */
export function rockFacets(e: boolean, s: boolean, se: boolean): Piece {
  const key = (e ? 1 : 0) | (s ? 2 : 0) | (se ? 4 : 0);
  let p = pieces.get(key);
  if (!p) pieces.set(key, (p = makeRock(key, e, s, se)));
  return p;
}
function makeRock(key: number, e: boolean, s: boolean, se: boolean): Piece {
  const H = TILE / 2;
  const facets: Facet[] = [];
  // The south faces under the top's north half, where the quarter below is
  // not top; and the east faces beside its west half.
  const neFace = e && !(s && se);
  if (!s) facets.push(southFace(0, neFace ? TILE : H, H));
  else if (neFace) facets.push(southFace(H, TILE, H));
  const swFace = s && !(e && se);
  if (!e) facets.push(eastFace(H, 0, swFace ? TILE : H));
  else if (swFace) facets.push(eastFace(H, H, TILE));
  facets.push({ side: "top", pts: rect(0, 0, H, H) });
  if (e) facets.push({ side: "top", pts: rect(H, 0, TILE, H) });
  if (s) facets.push({ side: "top", pts: rect(0, H, H, TILE) });
  if (e && s && se) facets.push({ side: "top", pts: rect(H, H, TILE, TILE) });
  return { key, facets, edges: [] };
}

/**
 * Whether a ramp, a slope or a stair on level z climbs onto (x, y): the
 * rock there is the ledge its top edge meets.
 *
 * The way it climbs is asked of `world` when there is one, as the incline
 * itself is drawn (see inclineAt). Asked of what the frame shows, a ramp
 * beside you with the hilltop over its ledge lifted off as a roof had no
 * ground over the rock to climb onto, was taken to climb north, and the
 * rock it does climb onto east, south or west was drawn as a lone rock
 * keeping to its own square, half a tile short of the incline's top edge
 * (2026-11-02: "the ramp falls short of meeting with the terrain below the
 * level it sends the player to").
 */
function inclineOnto(shown: Shown, z: number, x: number, y: number, world?: LevelSource): boolean {
  for (const [dx, dy] of UPHILL) {
    const inc = inclineAt(shown, z, x - dx, y - dy, world);
    if (inc?.foot === z && UPHILL[inc.dir][0] === dx && UPHILL[inc.dir][1] === dy) return true;
  }
  return false;
}

/**
 * The rock an incline climbs onto, as a whole block: its top the whole
 * tile lifted half a tile north-west, and its south and east faces where
 * no rock carries on. A rock otherwise paints only what of its top it can
 * without reaching over the tiles north and west of it (see rockFacets),
 * which leaves a rock standing alone, or at the end of a ridge, a post a
 * quarter of a tile across; and the incline's top edge runs the whole
 * width of its side, so half of it climbed onto nothing (2026-10-27: "the
 * tile on top of the ramp doesn't have a solid base under it", of the
 * ramp up the rock beside Círdan at the Grey Havens). The block reaches
 * over its neighbours north and west as the incline does over its own.
 *
 * It also keeps to its own square where the mass carries on east, south
 * and south-east of it, as a rock does (rockFacets' quarters and faces,
 * from the same three neighbours). With the hilltop over the mass drawn,
 * the hilltop covers all of it; with the hilltop lifted off as a roof
 * beside you, the lifted tile alone left the strip of its own square on
 * the side the mass carries on open, and the incline's top edge ended in
 * a notch of the face behind it (2026-11-02: "the ramp falls short of
 * meeting with the terrain below the level it sends the player to").
 */
export function ledgeFacets(e: boolean, s: boolean, se = e && s, n = false, w = false, nw = false): Piece {
  const key = 8 | (e ? 1 : 0) | (s ? 2 : 0) | (se ? 4 : 0) | (n ? 16 : 0) | (w ? 32 : 0) | (nw ? 64 : 0);
  let p = pieces.get(key);
  if (!p) {
    const H = TILE / 2;
    // How far the block reaches north and west over its neighbours: half
    // a tile where the mass does not go on that way, and not at all where
    // it does, whose own top is there already at the same height; a
    // second picture laid over it showed as a seam.
    const n0 = n ? 0 : -H, w0 = w ? 0 : -H;
    const facets: Facet[] = [];
    // The faces first, so the tops go on over their inner corners: the
    // lifted tile's south and east faces where the mass ends, run on
    // under the quarter of its own square it fills where the mass goes
    // on, as rockFacets' are.
    const neFace = e && !(s && se);
    if (!s) facets.push(southFace(w0, neFace ? TILE : TILE - H, TILE - H));
    else if (neFace) facets.push(southFace(H, TILE, H));
    const swFace = s && !(e && se);
    if (!e) facets.push(eastFace(TILE - H, n0, swFace ? TILE : TILE - H));
    else if (swFace) facets.push(eastFace(H, H, TILE));
    facets.push({ side: "top", pts: rect(0, 0, TILE - H, TILE - H) });
    if (!n) facets.push({ side: "top", pts: rect(0, -H, TILE - H, 0) });
    if (!w) facets.push({ side: "top", pts: rect(-H, 0, 0, TILE - H) });
    if (!(n && w && nw)) facets.push({ side: "top", pts: rect(-H, -H, 0, 0) });
    if (e) facets.push({ side: "top", pts: rect(H, 0, TILE, H) });
    if (s) facets.push({ side: "top", pts: rect(0, H, H, TILE) });
    if (e && s && se) facets.push({ side: "top", pts: rect(H, H, TILE, TILE) });
    pieces.set(key, (p = { key, facets, edges: [] }));
  }
  return p;
}

/** Rock, a cave's wall and the palisade: the raised tiles that stand as a mass rather than as a thin wall. */
export function isRockish(t: number | undefined): boolean {
  return t === Tile.Rock || t === Tile.CaveWall || t === Tile.Palisade || t === Tile.Hillside || t === Tile.HobbitDoor || t === Tile.FrozenWaste;
}

/**
 * Whether the wall on (x, y) of level z is the masonry under a floor of
 * the level above, drawn as a mass the way rock is rather than as a thin
 * wall (2026-11-02: "fix the implementation of walls under tiles above.
 * all around the citadel there are instances of tiles extending beyond the
 * tiles beneath"). A thin wall is a post in the middle of its square, so a
 * stone floor laid on it, a whole tile, hung over the post on every side
 * and looked to stand on nothing: the circles of Minas Tirith, each a
 * level above the last, stand on a face of wall, and the White Tower's
 * top on its walls. Rock under a hilltop never did that, its mass filling
 * the square the floor lies on.
 *
 * Only a stone floor over it, on the ground or higher, and only a wall
 * that is not a building's: no open floor beside it with a floor or roof
 * of its own over it, which is a room the wall stands round (Dol Amroth's
 * tower, under its roof). The head of a stair over its foot is the top of
 * the flight, not a ceiling, and does not count.
 */
export function wallMass(shown: Shown, z: number, x: number, y: number): boolean {
  if (z < 0 || shown(z, x, y) !== Tile.Wall || shown(z + 1, x, y) !== Tile.Stone) return false;
  for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
    const n = shown(z, x + dx, y + dy);
    if (n === undefined || isRaised(n)) continue;
    const over = shown(z + 1, x + dx, y + dy);
    if (over !== undefined && over !== Tile.StairTop) return false;
  }
  return true;
}

/**
 * Which outside faces of the thin wall on (x, y) of level z stand flush
 * under the edge of a roof (see skirtFacets): bit 1 east, 2 south, and 4
 * with more wall over it rather than the roof itself. Only a
 * wall with a roof over it, or with more wall over it up to a roof (the
 * lower storey of a house two high), and only on a side where the
 * building ends: nothing raised beside it on its own level and nothing
 * over that, which leaves out the side into a room under the same roof.
 * Never with the roof lifted to see in (see hiddenRoofs), when the walls
 * are drawn as they stand.
 */
export function wallSkirt(shown: Shown, z: number, x: number, y: number): number {
  if (shown(z, x, y) !== Tile.Wall) return 0;
  let k = z + 1;
  while (k < z + 8 && shown(k, x, y) === Tile.Wall) k++;
  if (!isRoof(shown(k, x, y))) return 0;
  const open = (dx: number, dy: number) => !isRaised(shown(z, x + dx, y + dy)) && shown(z + 1, x + dx, y + dy) === undefined;
  const sides = (open(1, 0) ? 1 : 0) | (open(0, 1) ? 2 : 0);
  return sides && k > z + 1 ? sides | 4 : sides;
}

/** Whether (x, y) of level z is a thin wall: a wall that is not a mass (see wallMass). */
function thinWallAt(shown: Shown, z: number, x: number, y: number): boolean {
  return shown(z, x, y) === Tile.Wall && !wallMass(shown, z, x, y);
}

/** Whether a door item lies on (x, y) of level z: the renderer knows, from the items. */
export type DoorAt = (z: number, x: number, y: number) => boolean;

/**
 * A door standing in a wall: which face of the wall it is on (see
 * doorFacing), whether that wall is a thin one — a castle wall on either
 * side of the doorway — or a mass of rock, and the tile its lintel is
 * made of. Null for a door lying flat, or no door at all.
 */
export function standingDoor(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt): { side: "s" | "e"; thin: boolean; beside: number } | null {
  if (!doorAt || !doorAt(z, x, y)) return null;
  const side = doorFacing(shown, z, x, y);
  if (side === null) return null;
  const a = side === "s" ? shown(z, x - 1, y) : shown(z, x, y - 1);
  const b = side === "s" ? shown(z, x + 1, y) : shown(z, x, y + 1);
  const thin = a === Tile.Wall || b === Tile.Wall;
  const beside = thin ? Tile.Wall : isRaised(a) ? a! : b!;
  return { side, thin, beside };
}

/**
 * Where the top edge of the face a door stands on starts, in its tile's
 * pixels: on the south face of an east-west wall, the east face of a
 * north-south one, at the foot of a thin wall's top or at the middle of the
 * tile for rock. faceTransform lays the door's picture there.
 *
 * Half a tile back along the wall from the tile's own edge, so that the
 * door's foot — half a tile down and along from its top — is in the door's
 * own tile, the one it is opened from, and its top runs into the tile north
 * of it (or west, in an east-west wall). Asked for on 2026-09-27: "the foot
 * of a door should be in the tile it is interacted with from. the top can
 * extend into the tile to the north". It started at the tile's edge until
 * then, which put the foot half in the next tile along.
 */
export function doorPlane(side: "s" | "e", thin: boolean): Pt {
  const at = thin ? WB : TILE / 2;
  return side === "s" ? [-STOREY, at] : [at, -STOREY];
}

/**
 * A standing door's lintel and the face it hangs on: the top of the wall it
 * stands in, carried across the doorway, and under it the door on the
 * wall's own face (doorPlane), from one side of the doorway to the other.
 *
 * The first facet is the door's own, where its picture goes; the rest are
 * the wall's, drawn in the wall's stuff (see the renderer). With the door
 * set half a tile back along the wall (see doorPlane), the lintel runs
 * half a tile further back to cover it, and the last half tile of the face
 * the door no longer reaches is wall.
 */
export function doorFacets(side: "s" | "e", thin: boolean): Piece {
  const key = 3000 + (side === "s" ? 0 : 1) + (thin ? 2 : 0);
  let p = pieces.get(key);
  if (!p) pieces.set(key, (p = makeDoor(key, side, thin)));
  return p;
}
function makeDoor(key: number, side: "s" | "e", thin: boolean): Piece {
  const a = thin ? WA : 0, b = thin ? WB : TILE / 2, h = TILE - STOREY;
  if (side === "s") {
    return {
      key,
      facets: [southFace(-STOREY, h, b), southFace(h, TILE, b), { side: "top", pts: rect(-STOREY, a, TILE, b) }],
      edges: thin ? [[-STOREY, a, TILE, a, true], [-STOREY, b, TILE, b, false]] : [],
    };
  }
  return {
    key,
    facets: [eastFace(b, -STOREY, h), eastFace(b, h, TILE), { side: "top", pts: rect(a, -STOREY, b, TILE) }],
    edges: thin ? [[a, -STOREY, a, TILE, true], [b, -STOREY, b, TILE, false]] : [],
  };
}

/**
 * Which way the doorway on (x, y) of level z runs, when it is a gap in a
 * thin wall with a building's roof over it: "s" in a wall running east-
 * west, "e" in one running north-south, as doorFacing; null for anything
 * else. Asked for on 2026-10-07: "the door in the mathom-house when
 * standing outside doesn't line up with the tile you actually enter
 * through".
 *
 * From outside a building the roof covers the tops of its walls, and the
 * doorway is read off the wall's face under the eaves: it is the gap in
 * the south face of a south wall, or the east face of an east wall. A
 * thin wall's post stops short of the doorway at both ends, so that gap
 * was a tile and two thirds long, and at its foot it ran from the last
 * few pixels of the tile north of the doorway to the middle of the tile
 * south of it; worse, the roof over the doorway leaned on the walls beside
 * it (proppedBy) and stood a whole storey of wall face under itself, which
 * shut the doorway itself and left the gap a tile along, in the next
 * tile. Now the roof over a doorway is a lintel with nothing under it, and
 * the walls either side run their arms on to it (Join 3), so the gap is
 * exactly where a door's face would be (doorPlane): its foot is the
 * doorway's own tile, the one walked through, and its top runs half a tile
 * into the tile north of it (or west, in a wall running east-west).
 *
 * Only under a roof, and only that roof as it is shown: a gateway open to
 * the sky has no lintel for the arms to meet, and with the roof lifted to
 * see in (see hiddenRoofs) the walls are what they always were.
 */
export function roofedDoorway(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt): "s" | "e" | null {
  const t = shown(z, x, y);
  if (t === undefined || isRaised(t) || !isRoof(shown(z + 1, x, y))) return null;
  if (doorAt && doorAt(z, x, y)) return null;
  const wall = (dx: number, dy: number) => shown(z, x + dx, y + dy) === Tile.Wall;
  if (wall(-1, 0) && wall(1, 0)) return "s";
  if (wall(0, -1) && wall(0, 1)) return "e";
  return null;
}

/** Whether (x, y) of level z is part of a mass of rock, for the rock round it to be joined to: rock, or a door standing in it. */
function massAt(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt): boolean {
  const t = shown(z, x, y);
  if (isRockish(t) || wallMass(shown, z, x, y)) return true;
  if (!doorAt || t === undefined || isRaised(t)) return false;
  const d = standingDoor(shown, z, x, y, doorAt);
  return d !== null && !d.thin;
}

/** What the thin wall on (x, y) of level z joins, north, east, south and west. */
export function wallJoins(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt): [Join, Join, Join, Join] {
  const join = (dx: number, dy: number): Join => {
    const t = shown(z, x + dx, y + dy);
    if (t === Tile.Wall) return wallMass(shown, z, x + dx, y + dy) ? 2 : 1;
    if (isRockish(t)) return 2;
    if (t === undefined) return 0;
    const d = standingDoor(shown, z, x + dx, y + dy, doorAt);
    if (d !== null) return d.side === (dx ? "s" : "e") ? 1 : 0;
    return roofedDoorway(shown, z, x + dx, y + dy, doorAt) === (dx ? "s" : "e") ? 3 : 0;
  };
  const j: [Join, Join, Join, Join] = [join(0, -1), join(1, 0), join(0, 1), join(-1, 0)];
  // Rock is joined only by a wall that runs into it: one standing beside
  // rock as it runs past, with wall carrying on across that side, would
  // otherwise put out an arm into the rock at every tile, like the rungs
  // of a ladder. The dwarves' gate, a wall two thick along the mountain's
  // edge, is the one that showed it.
  const across = (a: Join, b: Join) => a === 1 || a === 3 || b === 1 || b === 3;
  if (across(j[1], j[3])) {
    if (j[0] === 2) j[0] = 0;
    if (j[2] === 2) j[2] = 0;
  }
  if (across(j[0], j[2])) {
    if (j[1] === 2) j[1] = 0;
    if (j[3] === 2) j[3] = 0;
  }
  return j;
}

/**
 * Which corners of the thin wall on (x, y) are filled — bit 1 north-west,
 * 2 north-east, 4 south-west, 8 south-east — for wallFacets: a corner with
 * wall on both sides of it and across it is inside a wall two thick, and
 * is filled solid by the tile north-west of it.
 */
export function wallFills(shown: Shown, z: number, x: number, y: number): number {
  const w = (dx: number, dy: number) => thinWallAt(shown, z, x + dx, y + dy);
  let f = 0;
  if (w(0, -1) && w(-1, 0) && w(-1, -1)) f |= 1;
  if (w(0, -1) && w(1, 0) && w(1, -1)) f |= 2;
  if (w(0, 1) && w(-1, 0) && w(-1, 1)) f |= 4;
  if (w(0, 1) && w(1, 0) && w(1, 1)) f |= 8;
  return f;
}

/**
 * What the raised tile on (x, y) of level z paints, or a door standing
 * there: see wallFacets, rockFacets and doorFacets. Null for anything else.
 */
export function raisedPiece(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt, world?: LevelSource): Piece | null {
  const t = shown(z, x, y);
  if (t === Tile.Wall && !wallMass(shown, z, x, y)) return wallFacets(...wallJoins(shown, z, x, y, doorAt), wallFills(shown, z, x, y), wallSkirt(shown, z, x, y));
  if (isRockish(t) || t === Tile.Wall) {
    const e = massAt(shown, z, x + 1, y, doorAt), s = massAt(shown, z, x, y + 1, doorAt);
    // A wall under a floor (wallMass) is a whole block, as the rock under
    // an incline's head is: the floor over it is a whole tile lifted half
    // a tile north-west, and a rock's top keeps to its own square, so
    // where nothing of the mass was north or west of it the floor's edge
    // there stood on nothing (2026-11-02: "the wall doesn't extend to be
    // underneath the tile above on the north side").
    // Every block is a whole block now, rock as well as a wall under a
    // floor, reaching half a tile over its neighbours north and west where
    // the mass does not go on there, as the hilltop on it does and as
    // Tibia's mountains do (2026-11-03: "do we just need to always draw
    // some overhang over the next tile west and north to keep it
    // simple?"). A rock that kept to its own square stood its north and
    // west edges a half tile short of the hilltop's: seen from below, with
    // the hilltop lifted off, the edge of a mountain on those sides was not
    // where it was from on top, and a ramp's top edge met nothing. What the
    // block covers of its neighbours is only drawn over: a click there is
    // still the neighbour's (see pickAt).
    const se = massAt(shown, z, x + 1, y + 1, doorAt);
    // Nor over a way to another level beside it — a ramp, a slope, a stair,
    // a ladder, a hole — which is drawn in its own square and would be
    // hidden under the rock's edge, nor over a bridge or a pit prop.
    const m = (dx: number, dy: number) => {
      const n = shown(z, x + dx, y + dy);
      return massAt(shown, z, x + dx, y + dy, doorAt) || (n !== undefined && NOT_GROUND.has(n)) || inclineAt(shown, z, x + dx, y + dy, world)?.foot === z;
    };
    return ledgeFacets(e, s, se, m(0, -1), m(-1, 0), m(-1, -1));
  }
  if (t === undefined) return null;
  const d = standingDoor(shown, z, x, y, doorAt);
  return d ? doorFacets(d.side, d.thin) : null;
}

/** Whether (px, py) lies inside the convex polygon pts, edges included. */
export function inPoly(px: number, py: number, pts: readonly Pt[]): boolean {
  let sign = 0;
  for (let i = 0; i < pts.length; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % pts.length];
    const c = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
    if (c === 0) continue;
    if (sign === 0) sign = Math.sign(c);
    else if (Math.sign(c) !== sign) return false;
  }
  return true;
}

/**
 * Which tile's raised thing, if any, paints the point (px, py) of the
 * square of (x, y) on level z: its own, or a neighbour's that runs on into
 * it. What the click asks, so a point on the floor beside a thin wall, or
 * in the corner a mass of rock leaves open, is not taken for the wall.
 */
export function paintedBy(shown: Shown, z: number, x: number, y: number, px: number, py: number, doorAt?: DoorAt, world?: LevelSource): [number, number] | null {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const p = raisedPiece(shown, z, x + dx, y + dy, doorAt, world);
      if (p && p.facets.some((f) => inPoly(px - dx * TILE, py - dy * TILE, f.pts))) return [x + dx, y + dy];
    }
  }
  return null;
}

/**
 * The tiles that are floor enough to be laid under a wall: not raised, and
 * not a way to another level, a bridge's span or a prop, whose pictures
 * make no sense cut into pieces.
 */
const NOT_GROUND = new Set<number>([
  Tile.Stair, Tile.StairTop, Tile.Hole, Tile.Ladder, Tile.LadderTop, Tile.Ramp, Tile.RampTop, Tile.Slope, Tile.SlopeTop, Tile.Bridge, Tile.PitProp,
]);
export function groundLike(t: number | undefined): boolean {
  return t !== undefined && !isRaised(t) && !NOT_GROUND.has(t);
}

/**
 * The floor under a raised tile, wherever it shows: a thin wall's square
 * is mostly floor, and a mass of rock leaves a corner of floor at a south-
 * west or north-east corner of itself. The square is cut where the wall's
 * foot runs — three quarters of the way across for a wall, half way for
 * rock — and each part takes the floor of the tile beside it on that side
 * (groundFrom says which). So the room's floor runs up to the wall on the
 * inside and the yard's on the outside. `from` is that tile, as an offset;
 * it is a rock where a wall is built against rock, or the rock itself for
 * a corner of rock with only walls beside it, and those are laid in shadow;
 * null when no tile round it is floor, when the caller picks one.
 *
 * Only the parts that can show are listed: all four under a wall, the
 * open corners of a rock — after the whole square of a rock at the edge of
 * its mass, for the reason given below.
 */
export function groundRegions(shown: Shown, z: number, x: number, y: number, doorAt?: DoorAt): { x0: number; y0: number; x1: number; y1: number; from: [number, number] | null }[] {
  const wall = thinWallAt(shown, z, x, y);
  const k = wall ? (TILE * 3) / 4 : TILE / 2;
  const out: { x0: number; y0: number; x1: number; y1: number; from: [number, number] | null }[] = [];
  const mass = (dx: number, dy: number) => massAt(shown, z, x + dx, y + dy, doorAt);
  if (!wall) {
    // A rock at the edge of its mass is laid on the floor beside it, all
    // of its square, under the rock and before its open corners are. None
    // of that is meant to show, and it does not; but at a zoom of one and
    // a half the edge of the rock's picture and the edge of the floor tile
    // beside it can both miss the one column of pixels between them, and
    // what is under that column is then this, not the black behind the
    // world.
    let from: [number, number] | null = null;
    for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
      if (!mass(dx, dy)) {
        from = groundLike(shown(z, x + dx, y + dy)) ? [dx, dy] : [0, 0];
        if (from[0] || from[1]) break;
      }
    }
    if (from) out.push({ x0: 0, y0: 0, x1: TILE, y1: TILE, from });
  }
  for (const [qx, qy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) {
    if (!wall) {
      // A rock's open corners: south-west with no rock south or west of
      // it, north-east with none north or east. Everywhere else in its
      // square something of the rock, or of its neighbour's, is painted.
      const sw = qx === 0 && qy === 1 && !mass(0, 1) && !mass(-1, 0);
      const ne = qx === 1 && qy === 0 && !mass(0, -1) && !mass(1, 0);
      if (!sw && !ne) continue;
    }
    out.push({ x0: qx ? k : 0, y0: qy ? k : 0, x1: qx ? TILE : k, y1: qy ? TILE : k, from: groundFrom(shown, z, x, y, qx ? k : 0, qy ? k : 0, doorAt) });
  }
  return out;
}

/**
 * Which neighbour's floor is laid under the point (px, py) of the raised
 * tile on (x, y): see groundRegions. For a thin wall it is the floor on the
 * same side of the wall as the point: a quarter beside an arm takes the
 * floor across from that arm — west of an arm running north, the tile to
 * the west — and a quarter in the crook of two arms the tile in the crook,
 * diagonally. A quarter with no arm beside it, at the end of a wall, takes
 * the floor across the wall's line first. Rock, with only its open corners
 * showing, takes the tile north or south first.
 */
export function groundFrom(shown: Shown, z: number, x: number, y: number, px: number, py: number, doorAt?: DoorAt): [number, number] | null {
  const wall = thinWallAt(shown, z, x, y);
  const k = wall ? (TILE * 3) / 4 : TILE / 2;
  const dx = px >= k ? 1 : -1, dy = py >= k ? 1 : -1;
  let order: (readonly [number, number])[] = [[0, dy], [dx, 0], [dx, dy]];
  if (wall) {
    const [n, e, s, w] = wallJoins(shown, z, x, y, doorAt);
    // The arm running along this quarter's side, up or down the square,
    // and the one running across it.
    const along = dy < 0 ? n : s, across = dx < 0 ? w : e;
    if (along && across) order = [[dx, dy], [0, dy], [dx, 0]];
    else if (along || (!across && (n || s))) order = [[dx, 0], [dx, dy], [0, dy]];
  }
  for (const [ax, ay] of order) {
    const t = shown(z, x + ax, y + ay);
    // A wall against rock has the rock on that side, not a floor: the
    // strip between them is laid with the rock, in shadow, so the wall
    // reads as built against the cliff rather than with a path behind it.
    if (groundLike(t) || (wall && (isRockish(t) || wallMass(shown, z, x + ax, y + ay)))) return [ax, ay];
  }
  // A corner of rock open to nothing but walls: the rock's own foot, in
  // the same shadow.
  if (!wall) return [0, 0];
  // Walled in on that side — a wall two thick, or one against rock — so
  // the nearest floor there is, which is the floor the wall stands in.
  for (const [ax, ay] of [[0, -1], [1, 0], [0, 1], [-1, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    if (groundLike(shown(z, x + ax, y + ay))) return [ax, ay];
  }
  for (let ay = -2; ay <= 2; ay++) {
    for (let ax = -2; ax <= 2; ax++) {
      if ((ax === -2 || ax === 2 || ay === -2 || ay === 2) && groundLike(shown(z, x + ax, y + ay))) return [ax, ay];
    }
  }
  return null;
}

/**
 * The picture of a raised tile's piece, made once per sprite and shape and
 * kept, so a frame is a copy per wall or rock rather than a clip and a
 * shear for each face of it. PIECE_PAD pixels bigger than the square on
 * every side, since a wall's arm reaches half a tile into rock to its
 * north or west and every face hangs half a tile past its top: it is drawn
 * PIECE_PAD up and left of the tile's square.
 *
 * The faces are the top's own texture sheared onto the side and shaded, as
 * faceSprites makes them, with a lighter lip where the face turns into the
 * top; the texture is laid from the tile's own corner, so the bricks of a
 * run of wall and the faces under them line up from one tile to the next.
 * A quarter of a rock's top under a floor of the level above is painted
 * all the same, and the floor goes on over it: leaving it out saved
 * nothing, the picture being one copy either way, and left the edge of the
 * floor above to meet nothing at a zoom where that edge falls on a half
 * pixel.
 */
export const PIECE_PAD = STOREY;
const pieceCache = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();
export function pieceOf(spr: HTMLCanvasElement, piece: Piece): HTMLCanvasElement {
  let byKey = pieceCache.get(spr);
  if (!byKey) pieceCache.set(spr, (byKey = new Map()));
  const key = piece.key;
  let c = byKey.get(key);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const pat = ctx.createPattern(spr, "repeat");
  for (const f of piece.facets) {
    if (f.side === "top") {
      polyPath(ctx, f.pts);
      ctx.fillStyle = pat ?? "#777";
      ctx.fill();
      continue;
    }
    paintFace(ctx, pat, f);
    if (f.under) {
      // The texture's first row is a light one, the lip of the face
      // above: the face's first pixel takes the one beside it instead, so
      // the storeys of a wall read as one face with no seam (2026-11-02,
      // Matt: "it's still visible, even if it's grey").
      const [x0, y0] = f.pts[0];
      ctx.save();
      polyPath(ctx, f.pts);
      ctx.clip();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const X = x0 + PIECE_PAD, Y = y0 + PIECE_PAD, n = TILE + 2 * PIECE_PAD;
      if (f.side === "s") ctx.drawImage(c, 0, Y + 1, n, 1, 0, Y, n, 1);
      else ctx.drawImage(c, X + 1, 0, 1, n, X, 0, 1, n);
      ctx.restore();
      continue;
    }
    const [x0, y0] = f.pts[0];
    const [x1, y1] = f.side === "s" ? f.pts[1] : f.pts[3];
    ctx.fillStyle = f.side === "s" ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.12)";
    if (f.side === "s") ctx.fillRect(x0, y0, x1 - x0, 1);
    else ctx.fillRect(x0, y0, 1, y1 - y0);
  }
  for (const [ax, ay, bx, by, dark] of piece.edges) {
    ctx.fillStyle = dark ? "rgba(20,16,14,0.6)" : "rgba(235,225,210,0.35)";
    // A line a pixel wide on the inside of the edge: below or right of a
    // north or west edge, above or left of a south or east one.
    const inset = dark ? 0 : -1;
    if (ay === by) ctx.fillRect(Math.min(ax, bx), ay + inset, Math.abs(bx - ax), 1);
    else ctx.fillRect(ax + inset, Math.min(ay, by), 1, Math.abs(by - ay));
  }
  byKey.set(key, c);
  return c;
}

/**
 * The tile whose picture a raised tile's top and faces are made of: its
 * own, but for a hobbit's round door, which is the hill's turf on top like
 * the rest of the hill, with the door on its face (see hobbitDoorPiece).
 * Its own picture, door and all, put the door on the roof as well as the
 * face (2026-10-01: "the round hobbit hole doors are rendering part of the
 * door on the roof").
 */
export function blockTile(t: number): number {
  return t === Tile.HobbitDoor ? Tile.Hillside : t;
}

/** A round door's paint, by the tile's variant, as its flat picture has it. */
const DOOR_PAINT = ["#2e7d32", "#b23b2e", "#2b5fa8", "#d08a2a"];
const doorPieces = new Map<string, HTMLCanvasElement>();

/**
 * A hobbit hole's round door, standing upright in the face of the hill
 * that looks out: the south face where there is one, which is the way the
 * holes along a lane face, and otherwise the east one, which is Bag End's
 * onto the road. It is a picture the size of pieceOf's, drawn over it, and
 * kept inside the face it is set in. Null where the hill shows no face.
 */
export function hobbitDoorPiece(piece: Piece, variant: number): HTMLCanvasElement | null {
  // On the corner of a hill a face is half a tile long, which is too short
  // to hold a door (2026-10-01: "the round door on the corner looks
  // wrong"): the longer of the two faces is taken, the south one when they
  // are the same.
  const faceLength = (f: Facet) => (f.side === "s" ? f.pts[1][0] - f.pts[0][0] : f.pts[3][1] - f.pts[0][1]);
  const faces = piece.facets.filter((f) => f.side === "s" || f.side === "e");
  if (!faces.length) return null;
  const face = faces.reduce((best, f) => (faceLength(f) > faceLength(best) || (faceLength(f) === faceLength(best) && f.side === "s" && best.side !== "s") ? f : best));
  const key = `${piece.key}:${variant & 3}`;
  let c = doorPieces.get(key);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.translate(PIECE_PAD, PIECE_PAD);
  polyPath(ctx, face.pts);
  ctx.clip();
  // Drawn in the face's own plane, sheared and squashed onto it exactly as
  // its turf is (see paintFace), so the door lies on the face at the
  // oblique angle the face is seen at rather than standing up off it
  // (2026-10-01: "fix the round doors to render on the face of the wall
  // they're on, at an oblique angle"). In that plane u runs along the face
  // and v down it, a storey being TILE of v.
  const [x0, y0] = face.pts[0];
  let mid: number, span: number;
  if (face.side === "s") {
    ctx.transform(1, 0, 0.5, 0.5, 0, y0);
    mid = (face.pts[0][0] + face.pts[1][0]) / 2;
    span = face.pts[1][0] - face.pts[0][0];
  } else {
    ctx.transform(0, 1, 0.5, 0.5, x0, 0);
    mid = (face.pts[0][1] + face.pts[3][1]) / 2;
    span = face.pts[3][1] - face.pts[0][1];
  }
  // Round, standing on the foot of the face, and no wider than it.
  const r = Math.min(10, span / 2 - 3), cv = TILE - r - 2.5;
  const disc = (rad: number) => {
    ctx.beginPath();
    ctx.ellipse(mid, cv, rad, rad, 0, 0, Math.PI * 2);
    ctx.fill();
  };
  ctx.fillStyle = "#d9c08a"; // the stone round it
  disc(r + 2);
  ctx.fillStyle = DOOR_PAINT[variant & 3];
  disc(r);
  ctx.fillStyle = "rgba(0,0,0,0.25)"; // its planks
  for (const du of [-5, 0, 5]) ctx.fillRect(mid + du, cv - r, 1, 2 * r);
  ctx.fillStyle = "#f2d24b"; // the brass knob in the middle
  disc(2.2);
  doorPieces.set(key, c);
  return c;
}

/** The polygon pts as the path on ctx. */
function polyPath(ctx: CanvasRenderingContext2D, pts: readonly Pt[]) {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
}

/**
 * A south or east face filled with the texture `pat` pushed over onto its
 * side, laid from the face's first point — the top of it — and shaded.
 */
function paintFace(ctx: CanvasRenderingContext2D, pat: CanvasPattern | null, f: Facet) {
  const [x0, y0] = f.pts[0];
  ctx.save();
  polyPath(ctx, f.pts);
  ctx.clip();
  // Sprite point (u, v) to (x0 + u + v/2, y0 + v/2) on a south face, and
  // (x0 + v/2, u + v/2) on an east one: the rows of the sprite run along
  // the face, and it is squashed to a storey.
  if (f.side === "s") ctx.transform(1, 0, 0.5, 0.5, 0, y0);
  else ctx.transform(0, 1, 0.5, 0.5, x0, 0);
  ctx.fillStyle = pat ?? "#777";
  ctx.fillRect(-2 * TILE, -2 * TILE, 5 * TILE, 5 * TILE);
  ctx.restore();
  polyPath(ctx, f.pts);
  ctx.fillStyle = `rgba(0,0,0,${FACE_SHADE[f.side as "s" | "e"]})`;
  ctx.fill();
}

/**
 * What stands at (x, y) on level z, as the frame being drawn sees it:
 * undefined for nothing, for a level that is not being drawn, and for a
 * roof that has been lifted so the viewer can see inside.
 */
export type Shown = (z: number, x: number, y: number) => number | undefined;

/**
 * What a floor above the lowest level stands on, when it is not a block.
 *
 * Most of the floors up there stand on one: a hilltop is the top of the
 * rock under it, a rampart the top of the palisade. Some do not, and they
 * are of two kinds that the map tells apart by where they are rather than
 * by what they are. A floor beside ground that does stand on rock — the
 * head of a ramp or a stair cut into the edge of a ledge — is part of that
 * ledge, and is drawn as a column of the same rock under it, from the
 * level below up to its own; this returns the tile its neighbour stands
 * on, for the column's faces to be made of. A floor with nothing of the
 * kind beside it — a roof over a room, a walk over a hall — is a slab
 * with open air under it, and this returns undefined.
 *
 * It asks only where things are, never which tile is which, so a new kind
 * of ramp or roof is drawn by the same rule the day it is added. The four
 * sides are tried north, east, south and west, the order the server tries
 * a ramp's sides in.
 */
export function proppedBy(shown: Shown, z: number, x: number, y: number): number | undefined {
  // The head of a ramp or a slope stands on nothing either, and it was
  // the commonest floor this answered for: the ledge's rock was stood
  // under it as a column, and the ramp read as a flat square in a box of
  // rock. Since 2026-09-28 ("fix ramps looking like they're inside a weird
  // box of a similar type of the neighbouring terrain. also render them
  // oblique") the head is the top edge of the incline its foot draws (see
  // inclineAt), and there is nothing under it to prop. The head of a
  // stair likewise, since the flight is drawn under it (2026-09-28: "render
  // stairs oblique, the one in the castle for example").
  if (inclineAt(shown, z, x, y)?.foot === z - 1) return undefined;
  // Nor is a roof over a doorway (2026-10-07: "the door in the
  // mathom-house when standing outside doesn't line up with the tile you
  // actually enter through"): it leaned on the walls either side and stood
  // a storey of wall face under itself, which shut the doorway on the
  // glass and left a gap beside it, a tile along, that read as the door.
  // It is the lintel over an opening, and the walls' arms meet under it:
  // see roofedDoorway.
  if (roofedDoorway(shown, z - 1, x, y)) return undefined;
  // Nor is the head of a ladder, the hatch it comes up through (2026-09-28:
  // "render the ladder oblique"): the ladder stands up under it, and a
  // column of the wall beside it stood there instead would bury the ladder.
  if (ladderAt(shown, z, x, y)?.foot === z - 1) return undefined;
  // Nor is any floor over ground that can be walked on (2026-10-08: "the
  // walls don't match the parts of the orc fort walls that can be walked
  // through"). The walk over the orc fort's gate leaned on the palisade
  // either side of it, and stood a storey of palisade in the gateway's
  // two outer tiles, so the gate looked a tile wide where it is three;
  // the corner towers did the same over the open sides of the rooms under
  // them, and a hilltop over a hall does it along the hall's edge. A
  // column there is a wall on the glass where the level below has a floor
  // you walk across, so the floor over it is a slab, whatever is beside
  // it. What is left to prop is a floor with solid ground or water under
  // it, which nobody walks through.
  if (walkable(shown(z - 1, x, y))) return undefined;
  for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]]) {
    const t = shown(z, x + dx, y + dy);
    if (t === undefined || isRaised(t)) continue;
    const under = shown(z - 1, x + dx, y + dy);
    if (isRaised(under)) return under;
  }
  return undefined;
}

/*
 * RAMPS AND SLOPES, DRAWN AS WHAT THEY ARE. Asked for on 2026-09-28: "fix
 * ramps looking like they're inside a weird box of a similar type of the
 * neighbouring terrain. also render them oblique".
 *
 * A ramp is two tiles on one spot: its foot on the lower level and its head
 * on the level above. Until then each was drawn as a floor, flat — the
 * foot on the ground between grey shoulders, the head a storey up with a
 * column of the ledge's rock stood under it (proppedBy) — so what showed
 * was a flat square of track in a box of rock. They are one thing: a
 * single surface, a tile long and a storey high, whose low edge lies along
 * the foot's downhill edge on the lower level and whose high edge lies
 * along the head's uphill edge on the level above, where the hilltop or
 * the ledge carries on. Seen from the south-east that surface is a
 * parallelogram, and the tile's picture is laid on it with a transform.
 *
 * Under the surface is a wedge of the ledge it is cut into. Of its faces
 * only those that look south or east can be seen: the side of a ramp
 * running north or south shows on its east, the side of one running west
 * or east on its south, and the back of the wedge — the vertical face at
 * its top, against the cliff — shows only for a ramp climbing south or
 * east, whose back is turned to the camera. Those are drawn in the ledge's
 * stuff, like a rock's own faces, so the ramp reads as cut into the cliff
 * rather than laid against it; the rest cannot be seen and are not drawn.
 * The top, the side and the back tile the foot's own square exactly, so
 * whatever the ground pass laid there is covered, and the surface leans
 * out of that square only up and to the left, over the ledge behind it —
 * which is drawn first, being further from the camera.
 *
 * All of it is in the foot's own pixels, (0, 0)–(TILE, TILE) on the
 * foot's level, the way the raised tiles' pieces are, so the drawing, the
 * click and the tests share one set of shapes.
 */

/** The way up, 0 north to 3 west, as a unit step on the grid. */
const UPHILL: Pt[] = [[0, -1], [1, 0], [0, 1], [-1, 0]];

/**
 * The ramp or slope whose incline climbs through the storey from level z
 * to z + 1 at (x, y), or whose top edge is level z there: where its foot
 * is and which way it climbs (see rampDirOn). Undefined for anything
 * else, and for a head with no foot under it, which the map never makes
 * and which is drawn as the floor it is. A stair is one as well, with
 * `stair` set, whose foot is Tile.Stair and whose head is Tile.StairTop
 * (see STAIRS); its head with no foot shown is the stairwell looked down
 * into (stairwellPicture).
 *
 * The way it climbs is asked of `world`, the whole of the levels, when
 * there is one to ask, rather than of what the frame shows: a hilltop
 * lifted off as a roof is still the ground the ramp climbs onto.
 */
export function inclineAt(shown: Shown, z: number, x: number, y: number, world?: LevelSource): { foot: number; dir: number; stair?: boolean } | undefined {
  const t = shown(z, x, y);
  // A stair is an incline too (2026-09-28: "render stairs oblique, the one
  // in the castle for example"), drawn as a flight of steps rather than a
  // smooth surface: see STAIRS below. It always climbs north, which is
  // the way the server's StepTarget sends a climber off its head.
  if (t === Tile.Stair) return { foot: z, dir: STAIR_DIR, stair: true };
  if (t === Tile.StairTop && shown(z - 1, x, y) === Tile.Stair) return { foot: z - 1, dir: STAIR_DIR, stair: true };
  let foot: number;
  if (isRampFoot(t)) foot = z;
  else if (isRampHead(t) && isRampFoot(shown(z - 1, x, y))) foot = z - 1;
  else return undefined;
  return { foot, dir: rampDirOn(world ?? { tileOn: shown }, foot, x, y) };
}

/**
 * How high the incline climbing `dir` stands, in storeys over its foot,
 * at the point (fx, fy) of its tile given as fractions of a tile from the
 * tile's north-west corner: nought along the downhill edge, one along the
 * uphill edge, and straight between.
 */
export function inclineHeight(dir: number, fx: number, fy: number): number {
  const h = dir === 0 ? 1 - fy : dir === 1 ? fx : dir === 2 ? fy : 1 - fx;
  return Math.min(1, Math.max(0, h));
}

/**
 * Where a point of the incline climbing `dir` is drawn, in the foot's own
 * pixels: the point (fx, fy) of the tile, lifted by its height.
 */
function onIncline(dir: number, fx: number, fy: number): Pt {
  const h = inclineHeight(dir, fx, fy);
  return [fx * TILE - h * STOREY, fy * TILE - h * STOREY];
}

/**
 * Everything an incline paints: its surface, the corners of the tile
 * lifted by their heights, north-west, north-east, south-east, south-west;
 * and the faces of its wedge that can be seen (see the note above), each
 * starting from the corner at its top, which is where a face's texture is
 * laid from (see pieceOf). And the transform that lays a picture TILE
 * square, drawn climbing north — its top row the uphill edge — onto the
 * surface the right way round.
 */
export interface Incline {
  top: Pt[];
  faces: Facet[];
  transform: [number, number, number, number, number, number];
}
const inclines: Incline[] = [];
export function inclineFacets(dir: number): Incline {
  dir &= 3;
  return (inclines[dir] ??= makeIncline(dir));
}
function makeIncline(dir: number): Incline {
  const top = [onIncline(dir, 0, 0), onIncline(dir, 1, 0), onIncline(dir, 1, 1), onIncline(dir, 0, 1)];
  const ground = (fx: number, fy: number): Pt => [fx * TILE, fy * TILE];
  const faces: Facet[] = [];
  // The back of the wedge, a storey of cliff under the top edge, for a
  // ramp whose top edge is on the south or east side of its tile.
  if (dir === 2) faces.push({ side: "s", pts: [top[3], top[2], ground(1, 1), ground(0, 1)] });
  if (dir === 1) faces.push({ side: "e", pts: [top[1], ground(1, 0), ground(1, 1), top[2]] });
  // The side: a triangle under the surface's east edge for a ramp running
  // north or south, its south edge for one running west or east, from the
  // corner at the top of it down to the ground and along to the foot.
  if (dir === 0) faces.push({ side: "e", pts: [top[1], ground(1, 0), ground(1, 1)] });
  if (dir === 2) faces.push({ side: "e", pts: [top[2], ground(1, 1), ground(1, 0)] });
  if (dir === 1) faces.push({ side: "s", pts: [top[2], ground(1, 1), ground(0, 1)] });
  if (dir === 3) faces.push({ side: "s", pts: [top[3], ground(0, 1), ground(1, 1)] });
  // The picture's top-left corner goes on the uphill edge's left end, as
  // one faces uphill, its top-right on that edge's right end, and its
  // bottom-left on the downhill edge below the first.
  const [ux, uy] = UPHILL[dir];
  const rx = -uy, ry = ux;
  const corner = (u: number, v: number) => onIncline(dir, 0.5 + (u - 0.5) * rx + (0.5 - v) * ux, 0.5 + (u - 0.5) * ry + (0.5 - v) * uy);
  const [ox, oy] = corner(0, 0), [ax, ay] = corner(1, 0), [bx, by] = corner(0, 1);
  return { top, faces, transform: [(ax - ox) / TILE, (ay - oy) / TILE, (bx - ox) / TILE, (by - oy) / TILE, ox, oy] };
}

/**
 * How much darker the surface of an incline is than a level floor, by
 * which way it climbs: a little for one that faces the camera and the
 * light from the south-west (climbing north or east), more for one that
 * faces away (climbing south, its back to the light, or west, the way the
 * east faces look).
 */
export const INCLINE_SHADE = [0.04, 0.06, 0.2, 0.2];

/**
 * The height a figure standing at (px, py) on level z is drawn at, in
 * storeys: its level, except over the incline of a ramp or a slope, where
 * it is the incline's own height under the middle of the figure's tile.
 * Nobody stands on a ramp — a step onto one comes out at the top or the
 * bottom (StepTarget on the server) — but the walk out of it is drawn from
 * the ramp's own tile (the via in game.ts), and on the level it comes out
 * on: a climber half a storey under the head at the start of the walk, and
 * somebody coming down half a storey over the foot. Following the surface
 * instead, a climb goes up the incline and a descent comes down it.
 */
export function standHeight(shown: Shown, px: number, py: number, z: number, world?: LevelSource): number {
  const x = Math.floor(px + 0.5), y = Math.floor(py + 0.5);
  const ramp = inclineAt(shown, z, x, y, world);
  if (!ramp) return z;
  return ramp.foot + inclineHeight(ramp.dir, px + 0.5 - x, py + 0.5 - y);
}

/*
 * LADDERS, STOOD UP. Asked for on 2026-09-28: "render the ladder oblique".
 *
 * A ladder is two tiles on one spot, like a ramp: Tile.Ladder on the lower
 * level and its head on the level above — Tile.LadderTop, a hatch in a
 * floor, or a Tile.Hole in the ground where the ladder comes up out of a
 * cellar. Going up lets out one tile north of the head and coming down one
 * tile south of the foot (StepTarget on the server): a climb carries on the
 * way it was going, in at the foot from the south and out at the top to
 * the north, and you go back down the way you came up, stepping south into
 * the hatch from the tile you landed on. Both ends let out south until
 * 2026-10-19: "exiting a ladder on the level above should put you north of
 * the hole". The ladder rises a storey, from the floor of its level to the
 * hatch. It is a south face, like a door's: LADDER_BACK in from the tile's
 * north edge and LADDER_W across the middle of the tile, and its picture
 * is laid on with faceTransform.
 *
 * Its foot is flush with its tile's north edge (2026-10-19: "put the
 * ladder flush with the north end of the tile"), against whatever is north
 * of it, and its top a storey up comes out at the north edge of the hatch,
 * where the landing begins. It stood in the middle of its tile from
 * 2026-09-28 ("since you can't stand on a ladder tile, center the foot of
 * ladder in the tile so it's clear why we can't fit next to it"), which
 * was while both ends let out south. A storey up is half a tile north on
 * the glass, so it now rises over half of the tile north of it, and
 * whoever stands there is behind it (see coversNorth).
 *
 * The hatch is an opening, not a floor, so it is drawn as a rim with the
 * ladder seen through it, and nothing is stood under it (proppedBy). Where
 * the level with the foot is not being drawn — a hatch or a hole looked at
 * from above, underground or on the surface — the ladder is seen going
 * down into the shaft instead: see ladderShaftSprite in sprites.ts.
 */

/**
 * How far the face of a standing ladder is in from its tile's north edge,
 * in world pixels: none, so it stands flush against the north edge.
 */
export const LADDER_BACK = 0;
/** How wide a ladder is, rail to rail, in world pixels. */
export const LADDER_W = 16;
/** Where its west rail is, in from the tile's west edge. */
export const LADDER_X = (TILE - LADDER_W) / 2;

/**
 * The ladder whose foot is on (x, y) of level z, or whose head — a ladder
 * top or a hole — is there with the foot on the level below: the level of
 * its foot. Undefined for anything else.
 */
export function ladderAt(shown: Shown, z: number, x: number, y: number): { foot: number } | undefined {
  const t = shown(z, x, y);
  if (t === Tile.Ladder) return { foot: z };
  if ((t === Tile.LadderTop || t === Tile.Hole) && shown(z - 1, x, y) === Tile.Ladder) return { foot: z - 1 };
  return undefined;
}

/**
 * The standing ladder's outline in its foot's own pixels: the face it is
 * drawn on, a storey tall, from its top edge on the west to its foot. And
 * the top-left corner of that face, where faceTransform lays its picture.
 */
export function ladderFace(): { pts: Pt[]; at: Pt } {
  const a = LADDER_X, b = LADDER_X + LADDER_W, y = LADDER_BACK;
  return { pts: [[a - STOREY, y - STOREY], [b - STOREY, y - STOREY], [b, y], [a, y]], at: [a - STOREY, y - STOREY] };
}

/**
 * Whether what stands up on a tile of kind t reaches over the tile north of
 * it on the glass, where it covers a figure standing there, who is behind
 * it: then that figure is drawn before it (see tallFront in render.ts).
 * Read off the outline it is drawn by, so the answer moves with it: a
 * ladder did while it stood at the north side of its tile, and with its
 * foot in the middle (2026-09-28: "since you can't stand on a ladder tile,
 * center the foot of ladder in the tile so it's clear why we can't fit
 * next to it") its top comes only to the tile's north edge, and it does
 * not.
 */
export function coversNorth(t: number | undefined): boolean {
  if (t === Tile.Ladder) return ladderFace().pts.some(([, py]) => py < 0);
  // A pit prop, stood up at the north side of its tile, rises a storey
  // over the tile north of it (2026-09-28: "render the pit prop oblique").
  if (t === Tile.PitProp) return propFacets().some((f) => f.pts.some(([, py]) => py < 0));
  // So does a flight of stairs, whose top riser rises a storey along the
  // north edge of its tile (2026-09-28: "render stairs oblique, the one in
  // the castle for example"): see stairFacets.
  if (t === Tile.Stair) return stairFacets().facets.some((f) => f.pts.some(([, py]) => py < 0));
  return false;
}

/*
 * PIT PROPS, STOOD UP. Asked for on 2026-09-28: "render the pit prop
 * oblique".
 *
 * A pit prop is a set of timbers holding up the roof of a heading in the
 * mine: two legs and a cap across them (TilePitProp on the server, which
 * says why it is walkable scenery rather than a pillar). Flat, it is drawn
 * against the top of its tile, the cap along the north edge and the legs
 * down from it. Stood up it is the same set in the same place: a frame
 * across the north side of its tile, PROP_BACK in from the north edge and
 * PROP_DEPTH of timber thick, its legs rising from the floor to the cap
 * and the cap a storey up, under the roof it holds. Whoever stands on the
 * tile stands in front of it, and whoever stands on the tile north of it
 * is behind it — it rises over that tile on the glass (coversNorth).
 *
 * It is drawn as its facets, like a block: each timber's south face, its
 * east face in shadow, and the top of the cap, in the timber of the flat
 * picture, and it is darkened by the dark where it stands as it goes on.
 */

/** How far the south face of a pit prop's timbers is in from its tile's north edge, in world pixels. */
export const PROP_BACK = 6;
/** How thick its timbers are, north to south, in world pixels. */
export const PROP_DEPTH = 4;
/** Its two legs, west and east edges, in from the tile's west edge: where the flat picture has them. */
export const PROP_LEGS: readonly Pt[] = [
  [4, 9],
  [TILE - 9, TILE - 4],
];
/** Its cap, west and east ends. */
export const PROP_CAP: Pt = [2, TILE - 2];
/** How deep the cap is, in storeys: the legs stand from the floor up to it. */
export const PROP_CAP_H = 0.2;

/** One facet of a standing pit prop, and which way it faces. */
export interface PropFacet {
  pts: Pt[];
  side: "s" | "e" | "top";
}

/**
 * The standing pit prop's facets in its tile's own pixels, back to front:
 * each leg's south and east faces, then the cap's, then the cap's top. A
 * point h storeys up is drawn h storeys' worth up and to the left.
 */
export function propFacets(): PropFacet[] {
  const at = (px: number, py: number, h: number): Pt => [px - h * STOREY, py - h * STOREY];
  const s = PROP_BACK, n = PROP_BACK - PROP_DEPTH;
  const box = (a: number, b: number, h0: number, h1: number, top: boolean): PropFacet[] => {
    const out: PropFacet[] = [
      { side: "s", pts: [at(a, s, h0), at(b, s, h0), at(b, s, h1), at(a, s, h1)] },
      { side: "e", pts: [at(b, s, h0), at(b, n, h0), at(b, n, h1), at(b, s, h1)] },
    ];
    if (top) out.push({ side: "top", pts: [at(a, n, h1), at(b, n, h1), at(b, s, h1), at(a, s, h1)] });
    return out;
  };
  const legTop = 1 - PROP_CAP_H;
  return [...PROP_LEGS.flatMap(([a, b]) => box(a, b, 0, legTop, false)), ...box(PROP_CAP[0], PROP_CAP[1], legTop, 1, true)];
}

/**
 * Whether a point on the plane of a standing pit prop's south face is on
 * its timber: `across` in pixels from the tile's west edge, `h` in storeys
 * over its floor. A leg is timber from the floor to the cap, and the cap
 * the whole of its length at the top; between the legs is the opening.
 */
export function onPropTimber(across: number, h: number): boolean {
  if (h < 0 || h > 1) return false;
  if (h >= 1 - PROP_CAP_H) return across >= PROP_CAP[0] && across <= PROP_CAP[1];
  return PROP_LEGS.some(([a, b]) => across >= a && across <= b);
}

/** The pit prop's timber: the flat picture's colours, lit on top and shadowed on the east. */
const PROP_FILL = { s: "#5a4326", e: "#3a2a17", top: "#74572f" };
let propCache: HTMLCanvasElement | undefined;
/**
 * The standing pit prop's picture, made once and kept: PIECE_PAD bigger
 * than the tile on every side, like a raised tile's piece, since it leans
 * up and to the left out of its square.
 */
export function propPicture(): HTMLCanvasElement {
  if (propCache) return propCache;
  const c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const path = (pts: readonly Pt[]) => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
  };
  for (const f of propFacets()) {
    path(f.pts);
    ctx.fillStyle = PROP_FILL[f.side];
    ctx.fill();
    if (f.side !== "s") continue;
    // The south faces: a shadowed line down the grain of each, as the flat
    // picture's legs have, so they read as round timber; and a lit edge
    // along the top, where the lamp catches it.
    const [p0, p1, p2, p3] = f.pts;
    ctx.save();
    path(f.pts);
    ctx.clip();
    ctx.strokeStyle = "rgba(20,12,4,0.45)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    const tall = p0[1] - p3[1] > PROP_CAP_H * STOREY + 1e-6;
    if (tall) {
      // A leg: the grain runs up it.
      const u = 0.65;
      ctx.moveTo(p0[0] + (p1[0] - p0[0]) * u, p0[1]);
      ctx.lineTo(p3[0] + (p2[0] - p3[0]) * u, p3[1]);
    } else {
      // The cap: the split along it, old timber under load.
      const v = 0.5;
      ctx.moveTo(p0[0] + (p3[0] - p0[0]) * v + 7, p0[1] + (p3[1] - p0[1]) * v);
      ctx.lineTo(p1[0] + (p2[0] - p1[0]) * v - 7, p1[1] + (p2[1] - p1[1]) * v);
    }
    ctx.stroke();
    ctx.fillStyle = "rgba(255,230,180,0.18)";
    ctx.beginPath();
    ctx.moveTo(p3[0], p3[1]);
    ctx.lineTo(p2[0], p2[1]);
    ctx.lineTo(p2[0], p2[1] + 1);
    ctx.lineTo(p3[0], p3[1] + 1);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  return (propCache = c);
}

/*
 * STAIRS, AS A FLIGHT OF STEPS. Asked for on 2026-09-28: "render stairs
 * oblique, the one in the castle for example".
 *
 * A stair is two tiles on one spot, like a ramp: Tile.Stair on the lower
 * level and Tile.StairTop, its head, on the level above. It always climbs
 * north — a step onto the foot lets out north of the head a level up, and
 * a step onto the head lets out south of the foot (StepTarget on the
 * server) — so it is an incline climbing north (inclineAt), a storey high
 * and a tile long. Until then both ends were drawn flat: the foot a square
 * of steps on the ground and, with the level above in view, the head a
 * square of floor a storey up with a column of the ledge's rock under it,
 * which is the box the ramps were in; and the head in the castle's temple
 * floor, whose foot is in the undercroft and not drawn from up there, a
 * flat picture of treads in a dark square.
 *
 * Here it is a flight of STAIR_STEPS steps, each a tread and the riser
 * under it, inside the incline's wedge: the nosing of every tread lies on
 * the incline a ramp would have, so a figure walking up it (standHeight)
 * has its feet on the edges of the steps, the top riser rises to the floor
 * of the level above along the tile's north edge, and the lowest tread is
 * the ground at its foot. The risers face south and the side of the flight
 * faces east, which is what the camera sees of it; the other side and the
 * back are turned away and not drawn. Treads, risers and the east side
 * tile the foot's own square exactly, as the ramp's surface and wedge do,
 * and lean out of it only up and to the left.
 *
 * Seen from above with its foot out of view — the castle's stair looked
 * down into from the temple floor, since only the viewer's own level is
 * drawn underground — it is the same flight a storey down, seen through
 * the opening in the floor: see stairwellPicture.
 *
 * Everything is in the foot's own pixels, like the ramp's, so the drawing,
 * the click and the tests share one set of shapes.
 */

/** Which way a stair climbs, as an incline's dir: north, always. */
export const STAIR_DIR = 0;
/** How many steps a flight has in its tile: risers a quarter of a storey high, treads a quarter of a tile deep, as the flat picture has them. */
export const STAIR_STEPS = 4;

/**
 * How high the top of the flight is, in storeys over its foot, at fy tiles
 * from its tile's north edge: the tread over that point, the highest a
 * quarter of a storey under the level above and the lowest on the ground.
 */
export function stairHeight(fy: number): number {
  const i = Math.min(STAIR_STEPS - 1, Math.max(0, Math.floor(fy * STAIR_STEPS)));
  return 1 - (i + 1) / STAIR_STEPS;
}

/**
 * Everything a flight of stairs paints, in its foot's own pixels: the east
 * side of each step, the risers from the top one down, and the treads,
 * none of which overlaps another on the glass; and `top`, the outline of
 * the risers and treads together, for the hover to be drawn round.
 */
export interface Stair {
  facets: Facet[];
  top: Pt[];
}
let stairShape: Stair | undefined;
export function stairFacets(): Stair {
  if (stairShape) return stairShape;
  const at = (px: number, py: number, h: number): Pt => [px - h * STOREY, py - h * STOREY];
  const n = STAIR_STEPS, d = TILE / n;
  const sides: Facet[] = [], risers: Facet[] = [], treads: Facet[] = [];
  const east: Pt[] = [], west: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const y0 = i * d, y1 = y0 + d;
    const hi = 1 - i / n, lo = 1 - (i + 1) / n;
    risers.push({ side: "s", pts: [at(0, y0, hi), at(TILE, y0, hi), at(TILE, y0, lo), at(0, y0, lo)] });
    treads.push({ side: "top", pts: [at(0, y0, lo), at(TILE, y0, lo), at(TILE, y1, lo), at(0, y1, lo)] });
    // The east side of the step, from its tread down to the ground. The
    // lowest tread is the ground, and has none.
    if (lo > 0) sides.push({ side: "e", pts: [at(TILE, y0, lo), at(TILE, y1, lo), at(TILE, y1, 0), at(TILE, y0, 0)] });
    east.push(at(TILE, y0, hi), at(TILE, y0, lo));
    west.push(at(0, y0, hi), at(0, y0, lo));
  }
  east.push(at(TILE, TILE, 0));
  west.push(at(0, TILE, 0));
  return (stairShape = { facets: [...sides, ...risers, ...treads], top: [...east, ...west.reverse()] });
}

/**
 * Whether what stands up on a tile of kind t reaches over the tile west of
 * it on the glass, where it covers a figure standing there, who is behind
 * it: then that figure is drawn before it (see tallFront in render.ts).
 * A flight of stairs leans a storey's worth to the left at its top
 * (2026-09-28: "render stairs oblique, the one in the castle for
 * example"). Read off the outline it is drawn by, like coversNorth.
 */
export function coversWest(t: number | undefined): boolean {
  if (t === Tile.Stair) return stairFacets().facets.some((f) => f.pts.some(([px]) => px < 0));
  return false;
}

/**
 * The stone of a flight: its treads lighter as they climb, as the flat
 * picture's are, and its risers the stone in the shadow of the tread over
 * each; with the nosing of each tread caught by the light.
 */
const STAIR_TREADS = ["#aeaeae", "#a4a4a4", "#9a9a9a", "#909090"];
const STAIR_RISER = "#6d6d6d";
const STAIR_JOINT = "rgba(30,30,30,0.35)";

/** Paint a flight's risers and treads, over whatever is already there. */
function paintSteps(ctx: CanvasRenderingContext2D) {
  const d = TILE / STAIR_STEPS;
  let riser = 0, tread = 0;
  for (const f of stairFacets().facets) {
    if (f.side === "e") continue;
    polyPath(ctx, f.pts);
    if (f.side === "s") {
      ctx.fillStyle = STAIR_RISER;
      ctx.fill();
      ctx.fillStyle = `rgba(0,0,0,${FACE_SHADE.s})`;
      ctx.fill();
      // A joint up the riser, a different place on every step, so the
      // stone reads as blocks laid in courses.
      const [p0, p1, p2, p3] = f.pts;
      const u = [0.3, 0.7, 0.45, 0.8][riser++ % 4];
      ctx.strokeStyle = STAIR_JOINT;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p0[0] + (p1[0] - p0[0]) * u, p0[1]);
      ctx.lineTo(p3[0] + (p2[0] - p3[0]) * u, p3[1]);
      ctx.stroke();
      continue;
    }
    ctx.fillStyle = STAIR_TREADS[tread++ % STAIR_TREADS.length];
    ctx.fill();
    // The nosing, lit, along the tread's south edge; and a shadow along
    // its back, under the riser above.
    const [, , [x2, y2], [x3]] = f.pts;
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    ctx.fillRect(x3, y2 - 1, x2 - x3, 1);
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fillRect(x3, y2 - d, x2 - x3, 1);
  }
}

/**
 * The picture of a flight of stairs whose foot and head are both in view,
 * made once per ledge and kept, PIECE_PAD bigger than the tile on every
 * side like a raised tile's piece: the east side of each step in the stuff
 * of the ledge it climbs onto, as a ramp's wedge is, and the steps over
 * them in dressed stone.
 */
const stairCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
export function stairPicture(ledge: HTMLCanvasElement): HTMLCanvasElement {
  let c = stairCache.get(ledge);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const pat = ctx.createPattern(ledge, "repeat");
  for (const f of stairFacets().facets) {
    if (f.side !== "e") continue;
    paintFace(ctx, pat, f);
    // The lip where the side turns into the tread over it.
    const [[x0, y0], [, y1]] = f.pts;
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.fillRect(x0 - 1, y0, 1, y1 - y0);
  }
  paintSteps(ctx);
  stairCache.set(ledge, c);
  return c;
}

/**
 * The head of a stair looked down into, with its foot's level out of view
 * — the castle's stair from the temple floor. It is the opening in the
 * floor and, through it, the same flight a storey down: every point of it
 * drawn a storey's worth down and to the right of where stairFacets puts
 * it, and cut to the opening. Where the flight does not reach, what shows
 * is the west wall of the stairwell, in shadow; and it darkens as it goes
 * down. Everything is inside the tile's square, so it is laid with the
 * ground, as a ladder's shaft is (ladderShaftSprite in sprites.ts).
 */
let stairwellCache: HTMLCanvasElement | undefined;
export function stairwellPicture(): HTMLCanvasElement {
  if (stairwellCache) return stairwellCache;
  const c = document.createElement("canvas");
  c.width = c.height = TILE;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  // The west wall of the stairwell, facing east, in shadow.
  ctx.fillStyle = "#4a4a4a";
  ctx.fillRect(0, 0, TILE, TILE);
  ctx.fillStyle = `rgba(0,0,0,${FACE_SHADE.e})`;
  ctx.fillRect(0, 0, TILE, TILE);
  // The flight, a storey down, cut to the opening by the canvas's edge.
  ctx.save();
  ctx.translate(STOREY, STOREY);
  paintSteps(ctx);
  ctx.restore();
  // Darker the further down it goes: the foot of the flight, a storey
  // down, is at the bottom of the opening and nearly in the dark.
  const g = ctx.createLinearGradient(0, 0, 0, TILE);
  g.addColorStop(0, "rgba(10,8,6,0)");
  g.addColorStop(1, "rgba(10,8,6,0.55)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE, TILE);
  // The rim of the opening: the near edges, south and east, catch the
  // light; the far ones are the floor's edge going down into the dark.
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fillRect(0, TILE - 1, TILE, 1);
  ctx.fillRect(TILE - 1, 0, 1, TILE);
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(0, 0, TILE, 1);
  ctx.fillRect(0, 0, 1, TILE);
  return (stairwellCache = c);
}

/**
 * Where somebody walking out of a ladder is drawn, and at what height: the
 * walk the server sends is the half of a climb it can see, from the ladder
 * tile to the tile it lets out on, a level along (the via in game.ts):
 * north of it going up, south of it coming down. Its first half is drawn
 * as the climb itself — standing at the ladder, just in front of its face,
 * going up it or down it a storey — and the second as the step from there
 * to the tile it came out on. `ladder` says the first half is under way,
 * when the figure is on the ladder rather than in front of anything else
 * on its tile. Only where both ends are drawn: with one of them off the
 * screen there is nothing to climb, and the walk is drawn as it always
 * was, from the hatch or the foot.
 *
 * Anywhere else it is where it is, at the height standHeight gives it.
 */
export function standAt(shown: Shown, px: number, py: number, z: number, world?: LevelSource): { x: number; y: number; h: number; ladder: boolean } {
  const x = Math.round(px);
  if (Math.abs(px - x) < 1e-6) {
    // Feet a couple of pixels in front of the ladder's face: a figure is
    // drawn with its feet on the south edge of the tile it is given.
    const front = (y: number) => y - 1 + (LADDER_BACK + 3) / TILE;
    // Down: from the foot, on its own level, south to the tile below it.
    const y = Math.floor(py), t = py - y;
    if (shown(z, x, y) === Tile.Ladder && ladderAt(shown, z + 1, x, y)?.foot === z) {
      const at = front(y);
      if (t < 0.5) return { x, y: at, h: z + 1 - 2 * t, ladder: true };
      return { x, y: at + (y + 1 - at) * (2 * t - 1), h: z, ladder: false };
    }
    // Up: from the hatch, on the level above, north to the tile beyond it.
    const yc = Math.ceil(py), u = yc - py;
    if (ladderAt(shown, z, x, yc)?.foot === z - 1) {
      const at = front(yc);
      if (u < 0.5) return { x, y: at, h: z - 1 + 2 * u, ladder: true };
      return { x, y: at + (yc - 1 - at) * (2 * u - 1), h: z, ladder: false };
    }
  }
  return { x: px, y: py, h: standHeight(shown, px, py, z, world), ladder: false };
}

/**
 * The picture of an incline: its wedge's faces in the ledge's stuff, and
 * the surface over them, made once per surface, ledge and way round and
 * kept, PIECE_PAD bigger than the tile on every side like a raised tile's
 * piece, since the surface leans up and to the left out of its square.
 * The faces are laid as pieceOf lays a rock's, with the lip along the
 * edge where each turns into the surface.
 */
const inclineCache = new WeakMap<HTMLCanvasElement, WeakMap<HTMLCanvasElement, HTMLCanvasElement[]>>();
export function inclineOf(surface: HTMLCanvasElement, ledge: HTMLCanvasElement, dir: number): HTMLCanvasElement {
  dir &= 3;
  let byLedge = inclineCache.get(surface);
  if (!byLedge) inclineCache.set(surface, (byLedge = new WeakMap()));
  let byDir = byLedge.get(ledge);
  if (!byDir) byLedge.set(ledge, (byDir = []));
  let c = byDir[dir];
  if (c) return c;
  c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const inc = inclineFacets(dir);
  const pat = ctx.createPattern(ledge, "repeat");
  for (const f of inc.faces) {
    paintFace(ctx, pat, f);
    // The lip along the edge the face shares with the surface: from its
    // top corner to the corner of the tile at the foot of the surface.
    const [x0, y0] = f.pts[0];
    const [x1, y1] = f.pts.length === 3 ? f.pts[2] : f.side === "s" ? f.pts[1] : f.pts[3];
    ctx.strokeStyle = f.side === "s" ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  }
  ctx.save();
  polyPath(ctx, inc.top);
  ctx.clip();
  ctx.transform(...inc.transform);
  ctx.drawImage(surface, 0, 0);
  ctx.restore();
  polyPath(ctx, inc.top);
  ctx.fillStyle = `rgba(0,0,0,${INCLINE_SHADE[dir]})`;
  ctx.fill();
  byDir[dir] = c;
  return c;
}

/**
 * Whether the storey from level z to level z + 1 at (x, y) is solid: a
 * block, or the column under a floor propped against a ledge (proppedBy).
 * `base` is the lowest level drawn, which nothing stands under.
 */
export function solidAt(shown: Shown, base: number, z: number, x: number, y: number): boolean {
  const t = shown(z, x, y);
  if (isRaised(t)) return true;
  if (z < base) return false;
  const above = shown(z + 1, x, y);
  return above !== undefined && !isRaised(above) && proppedBy(shown, z + 1, x, y) !== undefined;
}

/**
 * What the pointer is over, seen from the ground: pickAt, but looking
 * through the roofs. A building's roof, slate or thatch, is a storey up and
 * covers its rooms, and from outside it the pointer over where a room is
 * met the roof and outlined a tile of it (2026-10-01: "when i'm outside a
 * building, hovering the mouse over tiles inside are outlining parts of the
 * roof instead of the tiles inside the building"). From the ground the roof
 * is taken off for the pick, and what is under the pointer then is the
 * room's floor; `through` says the roof was looked through, so the outline
 * is drawn over the roof rather than under it. From up on a roof, or
 * anywhere off the ground, it is pickAt.
 *
 * `roof` is the tile of the roof the pointer was on, when it was looked
 * through: what stands or lies up there is what a look at it is about
 * (2026-10-19: "make it so when you look from outside a building, and the
 * roof is shown, that we look at the stuff above, not the corresponding
 * tile on our level"). The caller knows who is where; see Game.upOnRoof.
 */
export function pickThrough(wx: number, wy: number, meZ: number, levels: readonly number[], shown: Shown, doorAt?: DoorAt, world?: LevelSource): { x: number; y: number; z: number; h: number; through: boolean; roof?: { x: number; y: number; z: number; h: number } } {
  const hit = pickAt(wx, wy, meZ, levels, shown, doorAt, world);
  if (meZ < 0 || hit.z <= meZ || !coversRoom(shown(hit.z, hit.x, hit.y))) return { ...hit, through: false };
  // Every roof and storey floor over the viewer's level, so from the
  // street the pointer goes through the Prancing Pony's thatch and the
  // floor of its room upstairs to its ground floor (2026-10-08).
  const roofless: Shown = (z, x, y) => (z > meZ && coversRoom(shown(z, x, y)) ? undefined : shown(z, x, y));
  return { ...pickAt(wx, wy, meZ, levels, roofless, doorAt, world), through: true, roof: hit };
}

/**
 * Whether the hover outline of the tile (x, y), met at height h, is drawn
 * over everything, last of all, rather than with the ground of the lowest
 * level `base`, under whatever stands on it.
 *
 * A floor looked at through its roof (`through`, see pickThrough) is
 * outlined over the roof, or the roof would cover the outline. So is
 * anything above the lowest floor (2026-10-01: "the outline when hovering
 * over terrain a level above should draw over the top of the higher level
 * terrain, not under it"): at its tile's own depth the hill or the rock in
 * front of it was painted over it.
 *
 * And so is a tile of the lowest floor whose own column is drawn over it
 * (2026-10-08: "fix part of the outline missing"). A wall or rock there
 * paints its own square — a wall's faces and top, a rock's faces, are
 * inside it — and a roof or a floor a level up over it is drawn half a
 * tile up and left, over the north-west of the square. The pointer on the
 * south-east of a building's corner wall, where the roof is not, is the
 * wall's tile and not through any roof, and its outline went on with the
 * ground: what was left of it was the line round the foot of the wall,
 * broken off where the wall and the roof lay over it. Everywhere else on
 * the lowest floor the outline is still drawn with the ground, so what
 * stands in front of the tile still stands in front of its outline.
 */
export function outlineOnTop(shown: Shown, base: number, x: number, y: number, h: number, through: boolean): boolean {
  if (through || h > base) return true;
  return isRaised(shown(h, x, y)) || shown(h + 1, x, y) !== undefined;
}

/** A building's roof: slate, or a hobbit's thatch. */
export function isRoof(t: number | undefined): boolean {
  return t === Tile.Roof || t === Tile.Thatch;
}

/**
 * What covers a room from above: a roof, or the floorboards of a storey
 * over it. A building had nothing over its rooms but its roof until
 * 2026-10-08, when the Prancing Pony went up at Bree with a room upstairs
 * ("put barliman butterbur in a small inn at bree, with an upstairs that's
 * a single room with beds"); wooden floor above the ground is that room's
 * floor and nothing else.
 */
export function coversRoom(t: number | undefined): boolean {
  return isRoof(t) || t === Tile.Depot;
}

/**
 * Whether a creature on level z is under a roof that is drawn over it, and
 * so is drawn as an outline over the roof rather than as itself under it
 * (2026-10-07: "creatures should be showing outlines when under a roof
 * like this, not a partially visible sprite"). A figure is taller than its
 * tile and the roof is a storey up, so on the glass the roof over a room
 * covers some of whoever stands in it and not the rest: the tiles of it
 * over the room's edge leaned on the walls (see proppedBy) and went on at
 * their own depth, before the figure under them, and its head and
 * shoulders came out on top of the thatch with its legs under it.
 *
 * `shown` already leaves out a roof lifted so the viewer can see in — the
 * one over their own head, a building they can see into through its
 * doorway — so under one of those a creature is in plain sight and drawn
 * as itself. A walker counts as under the roof for the whole of a step
 * that starts or ends under it: halfway out of a doorway it is half
 * under the eaves, and a picture that changed at the middle of the step
 * would be the half-hidden figure again for the other half. Only roofs:
 * a hilltop has rock under it and nobody stands there.
 */
export function underRoof(shown: Shown, z: number, p: { moving: boolean }, from: { x: number; y: number }, to: { x: number; y: number }): boolean {
  const roofed = (x: number, y: number) => coversRoom(shown(z + 1, x, y));
  return roofed(to.x, to.y) || (p.moving && roofed(from.x, from.y));
}

/**
 * Which tile the point (wx, wy) of the glass is showing, given in tiles as
 * a flat top-down view would read it — the camera's own level, where
 * nothing is lifted — for a viewer standing on level meZ with `levels`
 * drawn, lowest first.
 *
 * It is a ray cast, and it has to be. A point on the glass can be showing
 * the ground, the south face of the wall in front of that ground, or the
 * top of a crag two storeys up and a whole tile further south-east, and
 * which of them it is depends on what is standing where. So the line of
 * sight through the point is followed down from above the highest thing
 * that could be drawn — it runs north-west as it falls, half a tile for
 * every storey — and the first thing it meets is the answer: a floor as it
 * crosses that floor's level, a block as it enters that block's storey,
 * through the top or through a face. That is front to back by
 * construction, which is what the painter's order is back to front.
 *
 * Steps of an eighth of a storey put a face's edge within two world pixels
 * of where it is drawn, which is closer than a finger or a hand holds
 * still; it is at most a few dozen lookups for a click and is never run
 * while drawing.
 *
 * What comes back is the tile, the level it is on, and the height of the
 * surface that was hit, in storeys: a floor's own level, or the top of a
 * block, which is one more than the block's level. When nothing at all is
 * met — a cave floor never seen — the ground under the point at the lowest
 * level is the answer, which is what a click there always meant.
 *
 * The incline of a ramp or a slope (2026-09-28, see inclineAt) is met
 * where the line of sight falls below its surface, and answers with the
 * foot's tile and level wherever on the incline that is; `world` is what
 * says which way it climbs. A ladder stood up (2026-09-28, see ladderAt)
 * is met where the line of sight crosses its face, and answers with its
 * own tile.
 */
export function pickAt(wx: number, wy: number, meZ: number, levels: readonly number[], shown: Shown, doorAt?: DoorAt, world?: LevelSource): { x: number; y: number; z: number; h: number } {
  const lo = levels.length ? Math.min(...levels) : meZ;
  const hi = levels.length ? Math.max(...levels) + 1 : meZ;
  const STEPS = 8;
  // A block is never clicked by its top or its faces (2026-09-27:
  // "dungeon/cave/mountain/wall tiles on the same level shouldn't be
  // occluding tiles on the same level. it also shouldn't be possible to
  // click the top of them"). What the pointer meets there is read as the
  // tile on the block's own level straight under it: a floor of that level
  // if there is one, or the block's own tile, flat; and where its level has
  // nothing at all, null, and the line of sight goes on down past it.
  //
  // Since the thin walls (2026-09-27: "implement the thin castle walls ...
  // rework mountain/cave walls for a similar effect") everything a raised
  // tile paints is inside its own square, and most of a wall's square is
  // floor: the floor either side of the wall, laid up to it (see
  // groundRegions). A point there that nothing raised paints is that
  // floor's tile, which is what the player sees under the pointer.
  const onPlane = (z: number): { x: number; y: number; z: number; h: number } | null => {
    const r = (z - meZ) / 2;
    const fx = wx + r, fy = wy + r;
    const x = Math.floor(fx), y = Math.floor(fy);
    const t = shown(z, x, y);
    if (t === undefined) return null;
    if (isRaised(t)) {
      const px = (fx - x) * TILE, py = (fy - y) * TILE;
      if (!paintedBy(shown, z, x, y, px, py, doorAt, world)) {
        const from = groundFrom(shown, z, x, y, px, py, doorAt);
        if (from) return { x: x + from[0], y: y + from[1], z, h: z };
      }
    }
    return { x, y, z, h: z };
  };
  // A ladder standing up out of its tile (2026-09-28, see ladderAt), met
  // where the line of sight crosses its face: the ladder's tile. The face
  // is on the tile's north edge since 2026-10-19 ("put the ladder flush with
  // the north end of the tile"), so once the line is below where it crossed
  // it is already over the tile north of the ladder: the ladder is looked
  // for on the row the line is over and the one south of it, and at a
  // level's floor as well as inside its storey.
  const ladderHit = (h: number, z: number, y: number): { x: number; y: number; z: number; h: number } | undefined => {
    for (const ly of [y, y + 1]) {
      const face = ly + LADDER_BACK / TILE;
      const hc = meZ + 2 * (face - wy);
      if (h > hc || hc < z || hc > z + 1) continue;
      const cx = wx + (face - wy), lx = Math.floor(cx), across = cx - lx;
      if (ladderAt(shown, z, lx, ly)?.foot === z && across >= LADDER_X / TILE && across <= (LADDER_X + LADDER_W) / TILE) return { x: lx, y: ly, z, h: z };
    }
    return undefined;
  };
  for (let i = hi * STEPS; i >= lo * STEPS; i--) {
    const h = i / STEPS;
    // Where the line through the point is when it is h storeys up: every
    // storey above the camera's level is half a tile further south-east.
    const r = (h - meZ) / 2;
    const x = Math.floor(wx + r), y = Math.floor(wy + r);
    if (i % STEPS === 0) {
      // On a level's floor: a floor tile of that level, or the top of a
      // block standing on the level below.
      const t = shown(h, x, y);
      const ladder = ladderHit(h, h, y);
      if (ladder) return ladder;
      // The head of a ramp is not a floor at its level any more but the
      // top edge of the incline (see inclineAt), met on the way down.
      if (t !== undefined && !isRaised(t) && inclineAt(shown, h, x, y)?.foot !== h - 1) return { x, y, z: h, h };
      if (solidAt(shown, lo, h - 1, x, y)) {
        const flat = isRaised(shown(h - 1, x, y)) ? onPlane(h - 1) : undefined;
        if (flat === null) continue;
        return flat ?? { x, y, z: h - 1, h };
      }
    } else {
      // Inside a storey: a block of that level, met through a face.
      const z = Math.floor(h);
      // Or the incline of a ramp or a slope climbing through it, met
      // where the line of sight falls below its surface: the foot's tile,
      // whichever part of the incline it is, which is the tile a click
      // there has always meant (the head is on the same spot).
      // A stair is met where it falls below the top of the step it is
      // over (2026-09-28: "render stairs oblique, the one in the castle
      // for example"), which is what the flight paints: see stairHeight.
      const ramp = inclineAt(shown, z, x, y, world);
      if (ramp && ramp.foot === z && h - z <= (ramp.stair ? stairHeight(wy + r - y) : inclineHeight(ramp.dir, wx + r - x, wy + r - y))) return { x, y, z, h: z };
      // Or a ladder standing up out of its tile (see ladderHit).
      const ladder = ladderHit(h, z, y);
      if (ladder) return ladder;
      // Or a pit prop stood up at the north side of its tile (2026-09-28:
      // "render the pit prop oblique"), met where the line of sight
      // crosses the south face of its timbers: the prop's own tile, which
      // is walkable. Through the opening between its legs the line goes on
      // to whatever is behind.
      if (shown(z, x, y) === Tile.PitProp) {
        const face = y + PROP_BACK / TILE;
        const hc = meZ + 2 * (face - wy);
        const across = wx + (face - wy) - x;
        if (h <= hc && onPropTimber(across * TILE, hc - z)) return { x, y, z, h: z };
      }
      if (solidAt(shown, lo, z, x, y)) {
        const flat = isRaised(shown(z, x, y)) ? onPlane(z) : undefined;
        if (flat === null) continue;
        return flat ?? { x, y, z, h: z + 1 };
      }
    }
  }
  // Nothing met on the way down: the ground under the point on the lowest
  // level — and where that is a wall's square, the floor beside the wall
  // if that is what shows there.
  const r = (lo - meZ) / 2;
  return onPlane(lo) ?? { x: Math.floor(wx + r), y: Math.floor(wy + r), z: lo, h: lo };
}

/**
 * One thing to paint, and where it goes in the order. The painter's rule
 * is the camera's: it stands to the south-east, so what is further south
 * plus further east is nearer and goes on later. `key` is that depth, x + y
 * with fractions for the things that have to fall between two tiles'
 * worth; `z` breaks a tie in favour of the higher level, which stands in
 * front of what is under it; and `sub` orders the things on one spot —
 * the ground, what lies on it, then who stands on it.
 */
export interface Job {
  key: number;
  z: number;
  sub: number;
  draw: () => void;
}

/** Back to front: nearer the south-east corner of the world goes later. */
export function byDepth(a: Job, b: Job): number {
  return a.key - b.key || a.z - b.z || a.sub - b.sub;
}

/**
 * The depth a creature standing on (x, y) is painted at. A figure is taller
 * than its tile and may be wider, so it goes on after the flat floor of the
 * tiles in front of it, which would otherwise paint over its feet and its
 * elbows: a tile and a half on. When something taller than the ground it
 * stands on is in front of it instead — a wall to the south, a crag to the
 * east, a tree — it goes on before that, so the thing in front covers it,
 * as it does.
 */
export function standingKey(x: number, y: number, tallInFront: boolean): number {
  return x + y + (tallInFront ? 0.7 : 1.5);
}

/**
 * The depth a walker between two tiles is painted at. Standing still, or
 * between two tiles with nothing tall in front of either, it is the more
 * south-easterly of them, so it goes on over the floor of both. But a
 * walker stepping into a tile with something tall in front of it — north
 * past a tree, west behind a wall — is behind that thing from the moment
 * it starts the step, not the moment it ends it; and one stepping out
 * from behind something is still partly behind it until it has gone
 * (2026-09-28: "when moving into a tile the player isn't occluded until
 * they arrive, they should be"). So while either end of the step has
 * something tall in front, the walker takes the nearer-the-back of the
 * keys that puts it behind, and the overlap of the two drawings decides
 * how much of it is hidden, which is exactly the part that is behind.
 *
 * `tallFront` and `standKey` are the scene's: whether something taller
 * stands just south or east of a tile, and the depth anything standing on
 * a tile starts from (see render.ts).
 */
export function walkerKey(
  p: { x: number; y: number; moving: boolean },
  from: { x: number; y: number },
  to: { x: number; y: number },
  tallFront: (x: number, y: number) => boolean,
  standKey: (x: number, y: number) => number,
): number {
  const tx = Math.ceil(p.x), ty = Math.ceil(p.y);
  const at = (x: number, y: number, tall: boolean) => standingKey(x, y, tall) + standKey(x, y) - (x + y);
  let key = at(tx, ty, tallFront(tx, ty));
  if (!p.moving) return key;
  for (const e of [from, to]) {
    if (tallFront(e.x, e.y)) key = Math.min(key, at(e.x, e.y, true));
  }
  return key;
}

/**
 * The outline of a face in world pixels, as a path on ctx: the south or
 * east side of the tile whose top-left corner is drawn at (X, Y), hanging
 * `k` storeys down from there. A face is a parallelogram, because the
 * bottom of it is k storeys lower and so k half-tiles further south-east.
 */
export function facePath(ctx: CanvasRenderingContext2D, X: number, Y: number, side: "s" | "e", k: number) {
  const d = k * STOREY;
  ctx.beginPath();
  if (side === "s") {
    ctx.moveTo(X, Y + TILE);
    ctx.lineTo(X + TILE, Y + TILE);
    ctx.lineTo(X + TILE + d, Y + TILE + d);
    ctx.lineTo(X + d, Y + TILE + d);
  } else {
    ctx.moveTo(X + TILE, Y);
    ctx.lineTo(X + TILE + d, Y + d);
    ctx.lineTo(X + TILE + d, Y + TILE + d);
    ctx.lineTo(X + TILE, Y + TILE);
  }
  ctx.closePath();
}

/** Whether the point (px, py) lies on the face facePath would outline. */
export function inFace(px: number, py: number, X: number, Y: number, side: "s" | "e", k: number): boolean {
  const d = k * STOREY;
  if (side === "s") {
    const down = py - (Y + TILE);
    return down >= 0 && down <= d && px - down >= X && px - down <= X + TILE;
  }
  const across = px - (X + TILE);
  return across >= 0 && across <= d && py - across >= Y && py - across <= Y + TILE;
}

/**
 * A block's two faces, made once from its top sprite and kept: the south
 * face 48 by 16 and the east face 16 by 48, each the top's own texture
 * pushed over onto its side and shaded, with a lighter lip along the top
 * edge where the face turns into the top.
 *
 * Using the top's own picture as the texture of the sides is the mockup's
 * choice and holds up for rock and brick, which is most of what stands up
 * in this world; a palisade's logs on their side read as timber, which is
 * what they are. Drawn once per sprite, not per frame: the shear is a
 * transform and a transform on every block every frame is the cost the
 * proposal said to avoid.
 *
 * The rows of the sprite run along the face and its columns up it, on
 * both sides, so a course of bricks stays a course of bricks whichever way
 * the wall is facing.
 */
const faceCache = new WeakMap<HTMLCanvasElement, { s: HTMLCanvasElement; e: HTMLCanvasElement }>();
/** How much darker each face is than the top: the east face more, away from the light. */
export const FACE_SHADE = { s: 0.26, e: 0.44 };
export function faceSprites(top: HTMLCanvasElement): { s: HTMLCanvasElement; e: HTMLCanvasElement } {
  let f = faceCache.get(top);
  if (f) return f;
  const make = (w: number, h: number, a: number, b: number, c: number, d: number, shade: number) => {
    const cv = document.createElement("canvas");
    cv.width = w;
    cv.height = h;
    const ctx = keptContext(cv);
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(a, b, c, d, 0, 0);
    ctx.drawImage(top, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = "source-atop";
    ctx.fillStyle = `rgba(0,0,0,${shade})`;
    ctx.fillRect(0, 0, w, h);
    return { cv, ctx };
  };
  // South: a sprite point (u, v) lands at (u + v/2, v/2) — along the edge,
  // and down and to the right as it goes down the face.
  const s = make(TILE + STOREY, STOREY, 1, 0, 0.5, 0.5, FACE_SHADE.s);
  s.ctx.fillStyle = "rgba(255,255,255,0.2)";
  s.ctx.fillRect(0, 0, TILE, 1);
  // East: (u, v) lands at (v/2, u + v/2) — the sprite's rows run down the
  // edge, north to south, and its columns out and down the face.
  const e = make(STOREY, TILE + STOREY, 0, 1, 0.5, 0.5, FACE_SHADE.e);
  e.ctx.fillStyle = "rgba(255,255,255,0.12)";
  e.ctx.fillRect(0, 0, 1, TILE);
  f = { s: s.cv, e: e.cv };
  faceCache.set(top, f);
  return f;
}

/**
 * A sprite as a repeating fill, for the thin edge of a slab: a roof's
 * eaves are the roof's own shingles, shaded. Kept per sprite, since a
 * pattern is made once and a slab's edge is filled every frame.
 */
const patternCache = new WeakMap<HTMLCanvasElement, CanvasPattern>();
export function patternOf(ctx: CanvasRenderingContext2D, spr: HTMLCanvasElement): CanvasPattern | string {
  let p = patternCache.get(spr);
  if (!p) {
    const made = ctx.createPattern(spr, "repeat");
    if (!made) return "#777";
    patternCache.set(spr, (p = made));
  }
  return p;
}

/**
 * A sprite's shape in black, for laying the dark over a figure or a thing
 * lying on a raised floor without laying it over the ground round it. The
 * ground's dark is a square a tile, and a square over a creature would put
 * a dark box round it; this is the creature's own outline, at whatever
 * alpha the dark is where it stands.
 */
const shadowCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
export function shadowOf(spr: HTMLCanvasElement): HTMLCanvasElement {
  let s = shadowCache.get(spr);
  if (s) return s;
  s = document.createElement("canvas");
  s.width = spr.width;
  s.height = spr.height;
  const ctx = keptContext(s);
  ctx.drawImage(spr, 0, 0);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, s.width, s.height);
  shadowCache.set(spr, s);
  return s;
}

/**
 * A sprite with the dark already laid over it, at `a` rounded to a
 * thirty-second: one copy per sprite and step, made the first time it is
 * asked for. The dark over a thing a storey up has to be laid as it is
 * painted, and laying it over a forest of trees or a range of cliff faces
 * a second time each, as a shadow or as a path, doubled what a zoomed-out
 * frame cost; a copy made once is one picture per thing. There are few
 * steps in practice — the surface's wash has four, the cave's a handful —
 * and a thirty-second of alpha is below what the eye tells apart.
 */
const dimCache = new WeakMap<HTMLCanvasElement, Map<number, HTMLCanvasElement>>();
export function dimmed(spr: HTMLCanvasElement, a: number): HTMLCanvasElement {
  const q = Math.round(Math.min(1, Math.max(0, a)) * 32);
  if (q === 0) return spr;
  let byStep = dimCache.get(spr);
  if (!byStep) dimCache.set(spr, (byStep = new Map()));
  let c = byStep.get(q);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = spr.width;
  c.height = spr.height;
  const ctx = keptContext(c);
  ctx.drawImage(spr, 0, 0);
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = `rgba(0,0,0,${q / 32})`;
  ctx.fillRect(0, 0, c.width, c.height);
  byStep.set(q, c);
  return c;
}

/**
 * The dark over one tile where it meets a different dark round it, as the
 * floor's is blended (see layBlended in render.ts): `nine` is the dark of
 * the tile and the eight round it, row by row from the north-west, and the
 * patch is a tile of black, each pixel as opaque as the tile's own dark at
 * its middle going over into its neighbour's across the edge between them,
 * the same sums a smoothed picture a pixel a tile makes. Made once per nine
 * darks, each to a thirty-second as dimmed rounds them.
 *
 * Asked for on 2026-10-21: "the soft edges to light doesn't work above
 * ground level?". The floor of the lowest level was blended on 2026-10-18;
 * whatever stands up off it — a hedge, a hill, a roof, a floor upstairs —
 * took one dark a tile and stood in steps at the edge of a light, and up
 * here, where the country is all hills, hedges, trees and houses, by night
 * that was most of what a light falls on.
 */
const patchCache = new Map<string, HTMLCanvasElement>();
export function washPatch(nine: readonly number[]): HTMLCanvasElement {
  const q = nine.map((a) => Math.round(Math.min(1, Math.max(0, a)) * 32));
  const key = q.join(",");
  let c = patchCache.get(key);
  if (c) return c;
  c = document.createElement("canvas");
  c.width = c.height = TILE;
  const ctx = keptContext(c);
  const pic = ctx.createImageData(TILE, TILE);
  // How far across the tile a pixel's middle is from the tile's, in tiles,
  // and so which neighbour it leans to and how much.
  const lean = (p: number) => (p + 0.5) / TILE - 0.5;
  for (let py = 0; py < TILE; py++) {
    const v = lean(py), row = v < 0 ? 0 : 2, fy = Math.abs(v);
    for (let px = 0; px < TILE; px++) {
      const u = lean(px), col = u < 0 ? 0 : 2, fx = Math.abs(u);
      const at = (r: number, k: number) => q[r * 3 + k] / 32;
      const mid = at(1, 1) * (1 - fx) + at(1, col) * fx;
      const side = at(row, 1) * (1 - fx) + at(row, col) * fx;
      pic.data[(py * TILE + px) * 4 + 3] = Math.round((mid * (1 - fy) + side * fy) * 255);
    }
  }
  ctx.putImageData(pic, 0, 0);
  patchCache.set(key, c);
  return c;
}

/** Whether the nine darks round a tile are all one, so its dark is flat. */
export function evenWash(nine: readonly number[]): boolean {
  const q = Math.round(Math.min(1, Math.max(0, nine[4])) * 32);
  return nine.every((a) => Math.round(Math.min(1, Math.max(0, a)) * 32) === q);
}

/**
 * A sprite with the dark laid over it as dimmed lays it, but blended where
 * its tile meets a different dark (see washPatch): the tile's square, whose
 * top-left corner is at (ox, oy) in the sprite, takes the patch as it is,
 * and whatever of the sprite stands up over the square, hangs below it or
 * reaches past its sides takes the patch's edge drawn out to it, so a
 * canopy is as dark as the north edge of its tile and a face as the south
 * edge of the top it hangs from. Where the nine darks are one it is
 * dimmed's own copy. Kept, a few per sprite, as the edges of the lights
 * come back to the same tiles frame after frame.
 */
const softCache = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();
export function softDimmed(spr: HTMLCanvasElement, nine: readonly number[], ox: number, oy: number): HTMLCanvasElement {
  if (evenWash(nine)) return dimmed(spr, nine[4]);
  const patch = washPatch(nine);
  let byKey = softCache.get(spr);
  if (!byKey) softCache.set(spr, (byKey = new Map()));
  const key = `${nine.map((a) => Math.round(Math.min(1, Math.max(0, a)) * 32)).join(",")}@${ox},${oy}`;
  let c = byKey.get(key);
  if (c) return c;
  // A sprite drawn on many lights' edges in turn keeps only its latest.
  if (byKey.size >= 32) byKey.clear();
  c = document.createElement("canvas");
  c.width = spr.width;
  c.height = spr.height;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(spr, 0, 0);
  ctx.globalCompositeOperation = "source-atop";
  const W = c.width, H = c.height, T = TILE;
  const x1 = ox + T, y1 = oy + T;
  // The square, then its edges drawn out to the sprite's, then its corners.
  ctx.drawImage(patch, ox, oy);
  if (oy > 0) ctx.drawImage(patch, 0, 0, T, 1, ox, 0, T, oy);
  if (y1 < H) ctx.drawImage(patch, 0, T - 1, T, 1, ox, y1, T, H - y1);
  if (ox > 0) ctx.drawImage(patch, 0, 0, 1, T, 0, oy, ox, T);
  if (x1 < W) ctx.drawImage(patch, T - 1, 0, 1, T, x1, oy, W - x1, T);
  if (ox > 0 && oy > 0) ctx.drawImage(patch, 0, 0, 1, 1, 0, 0, ox, oy);
  if (x1 < W && oy > 0) ctx.drawImage(patch, T - 1, 0, 1, 1, x1, 0, W - x1, oy);
  if (ox > 0 && y1 < H) ctx.drawImage(patch, 0, T - 1, 1, 1, 0, y1, ox, H - y1);
  if (x1 < W && y1 < H) ctx.drawImage(patch, T - 1, T - 1, 1, 1, x1, y1, W - x1, H - y1);
  byKey.set(key, c);
  return c;
}

/**
 * A see-through marker for somebody standing behind a wall: the sprite's
 * outline in a colour, a pixel outside its edge all round, over a faint
 * wash of the same colour where the body is. It is drawn over whatever
 * hid them, so a player never loses sight of themselves or of a friend
 * behind the castle's south wall, and the monster you are fighting stays
 * where you can see it; see the ghost pass in render.ts. One pixel bigger
 * each side than the sprite, so it is drawn a pixel up and left of it.
 */
const ghostCache = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();
export function ghostOf(spr: HTMLCanvasElement, color: string): HTMLCanvasElement {
  let byColor = ghostCache.get(spr);
  if (!byColor) ghostCache.set(spr, (byColor = new Map()));
  let out = byColor.get(color);
  if (out) return out;
  const w = spr.width + 2, h = spr.height + 2;
  const sil = document.createElement("canvas");
  sil.width = w;
  sil.height = h;
  const a = keptContext(sil);
  a.drawImage(spr, 1, 1);
  a.globalCompositeOperation = "source-in";
  a.fillStyle = color;
  a.fillRect(0, 0, w, h);
  out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const o = keptContext(out);
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) o.drawImage(sil, dx, dy);
  o.globalCompositeOperation = "destination-out";
  o.drawImage(sil, 0, 0);
  o.globalCompositeOperation = "source-over";
  o.globalAlpha = 0.3;
  o.drawImage(sil, 0, 0);
  byColor.set(color, out);
  return out;
}

/**
 * A tree stood up: the canopy and trunk cut out of the tile's own picture,
 * leaving its grass behind, so the tree can be drawn upright over the tiles
 * north of it and the grass laid flat with the rest of the ground. The cut
 * follows the shapes the tile paints (see Tile.Tree in sprites.ts): a disc
 * for the crown and a post for the trunk. It is drawn larger than the tile,
 * by TREE_SCALE, because a tree standing in a field is taller than a man
 * and a tile-sized one read as a shrub beside the people walking past it.
 * The cut is made at that size once, so painting a forest is a copy per
 * tree rather than a stretch per tree, which was the most expensive thing
 * on the screen when the window is zoomed out over a wood.
 */
const treeCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
export const TREE_SCALE = 1.3;
/** The size the cut-out is made at, in world pixels, square. */
export const TREE_PX = Math.round(TILE * TREE_SCALE);
export function treeCut(spr: HTMLCanvasElement): HTMLCanvasElement {
  let c = treeCache.get(spr);
  if (c) return c;
  const cut = document.createElement("canvas");
  cut.width = TILE;
  cut.height = TILE;
  const cx = keptContext(cut);
  cx.beginPath();
  cx.arc(16, 13, 12.5, 0, Math.PI * 2);
  cx.rect(13, 18, 6, 12);
  cx.clip();
  cx.drawImage(spr, 0, 0);
  c = document.createElement("canvas");
  c.width = TREE_PX;
  c.height = TREE_PX;
  const ctx = keptContext(c);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(cut, 0, 0, TREE_PX, TREE_PX);
  treeCache.set(spr, c);
  return c;
}
